// @ts-nocheck
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import config from "../config";
import { printPage } from "../utils/printPage";
import { AuthContext } from "../AuthProvider";
import { useTranslation } from "react-i18next";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import { fetchSchoolYears } from "../utils/fetchSchoolYears";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import GenderIcon from "../components/GenderIcon";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import { FaArrowCircleLeft, FaArrowRight, FaCalendarAlt, FaPrint, FaUsersCog } from "react-icons/fa";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import Modal from "../components/Modal";
import {
  assignChildToClassroom,
  placeChildFromWaitingList,
} from "../utils/assignChildToClassroom";
import { FaArrowRightFromBracket } from "react-icons/fa6";
import { calculateAge, parseToDate } from "../utils/dateUtils";
import { suggestClassroomForChild } from "../utils/suggestClassroom";
import { getKindergartenYearNumber } from "../utils/kindergartenUtils";
import { ageWithTurning4 } from "../utils/ageFormat";
import { formatAgeAtYearStart, nextBirthdayInYear } from "../utils/dateUtils";
import { translateClassName } from "../utils/classroomUtils";
import http from "../api/apiClient";
import { calculateEasterSunday } from "../utils/easterUtils";
import {
  listClassroomDrafts,
  createClassroomDraft,
  fetchClassroomDraftById,
  deleteClassroomDraft,
} from "../api/classroomDrafts";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import { useCan } from "../authz/useCan";
import {
  PlacementHero,
  PlacementPageShell,
} from "./planning/components/PlacementLds";

import { useBackNavigation } from "../components/navigation/BackButton";
const isActiveChild = (c) => c && !c.deceasedAt && !c.withdrawnAt;
const getYearSafe = (value) => {
  const date = parseToDate(value);
  return date ? date.getFullYear() : null;
};
const toDateSafe = (value) => parseToDate(value);
const startOfDay = (value) => {
  const date = toDateSafe(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};
const looksLikeUuid = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
    trimmed,
  );
};
const getChildDisplayName = (child) => {
  if (!child || typeof child !== "object") return null;
  const candidates = [
    child.fullName,
    child.fullname,
    child.displayName,
    child.display_name,
    child.name,
    [child.firstName, child.lastName].filter(Boolean).join(" "),
    [child.first_name, child.last_name].filter(Boolean).join(" "),
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const trimmed = candidate.trim();
    if (trimmed) return trimmed;
  }
  return null;
};
const buildChildLabel = (child) => {
  if (!child) return "";
  return [child.firstName, child.lastName].filter(Boolean).join(" ").trim();
};
const compareChildNames = (a, b, locale = "nl") => {
  const lastCmp = (a?.lastName || "").localeCompare(b?.lastName || "", locale);
  if (lastCmp !== 0) return lastCmp;
  return (a?.firstName || "").localeCompare(b?.firstName || "", locale);
};
const compareChildrenByExactBirthDate = (a, b, locale = "nl") => {
  const dateA = toDateSafe(a?.dateBirth);
  const dateB = toDateSafe(b?.dateBirth);

  if (dateA && dateB) {
    const dateCmp = dateA.getTime() - dateB.getTime();
    if (dateCmp !== 0) return dateCmp;
    return compareChildNames(a, b, locale);
  }

  if (dateA) return -1;
  if (dateB) return 1;
  return compareChildNames(a, b, locale);
};
const getUserDisplayName = (user) => {
  if (!user || typeof user !== "object") return null;
  const candidates = [
    [user.firstName, user.lastName].filter(Boolean).join(" "),
    [user.first_name, user.last_name].filter(Boolean).join(" "),
    user.displayName,
    user.display_name,
    user.fullName,
    user.fullname,
    user.username,
    user.email,
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const trimmed = candidate.trim();
    if (trimmed) return trimmed;
  }
  return null;
};
const AgePill = ({ dateBirth, schoolYear }) => {
  const { t, i18n } = useTranslation();
  const text = formatAgeAtYearStart(dateBirth, schoolYear, i18n.language); // bv: "3 → 4 in oktober"
  const nextBirthday = nextBirthdayInYear(dateBirth, schoolYear);
  const tooltip = nextBirthday
    ? nextBirthday.toLocaleDateString(i18n.language || "nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : t("classrooms.overview_management.age_at_year_start_tooltip");
  return (
    <span
      className="ml-2 text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded dark:text-blue-200 dark:bg-blue-900/60"
      title={tooltip}
    >
      {text}
    </span>
  );
};

const DraggableChild = ({
  child,
  onResetChild,
  onUnassignChild,
  showReset = false,
  isSuspicious = false,
  modifiedAssignments = [],
  childOriginalLocation = {},
  draftAssignments = {},
  suggestedClassName,
  schoolYear,
  rowToneClass = "",
}) => {
  const { t } = useTranslation();

  const [, drag] = useDrag(
    () => ({
      type: "CHILD",
      item: child,
    }),
    [child],
  );

  const originalType = childOriginalLocation?.[child.id]?.type;
  const isNowInClass = Object.values(draftAssignments).some(
    (children) =>
      Array.isArray(children) && children.some((c) => c.id === child.id),
  );

  const isNew =
    (originalType === "unassigned" || originalType === "waiting") &&
    isNowInClass;
  const isModified = modifiedAssignments.includes(child.id);
  const { text: ageText, exactDate } = ageWithTurning4(child.dateBirth);

  return (
    <li
      ref={drag}
      className={`py-2 border-b cursor-move flex items-center gap-2 ${rowToneClass} ${
        isSuspicious ? "bg-yellow-100 dark:bg-yellow-900" : ""
      }`}
      title={isSuspicious ? "Dit kind staat in meerdere klassen" : ""}
    >
      <GenderIcon gender={child.gender} />
      <span>
        {child.firstName} {child.lastName}
      </span>
      ({calculateAge(child.dateBirth)})
      <AgePill dateBirth={child.dateBirth} schoolYear={schoolYear} />
      {childOriginalLocation?.[child.id]?.type !== "classroom" && (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded ${
            suggestedClassName === "Too young"
              ? "text-red-800 bg-red-100 dark:text-red-300 dark:bg-red-800"
              : "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-700"
          }`}
        >
          {t("classrooms.overview_management.suggested_class")}:{" "}
          {translateClassName(suggestedClassName) ||
            t("classrooms.overview_management.no_suggestion")}
        </span>
      )}
      {child.current && !isModified && (
        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded dark:text-green-200 dark:bg-green-900/60">
          {t("common.badges.current")}
        </span>
      )}
      {isModified && (
        <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded dark:text-orange-200 dark:bg-orange-900/60">
          {t("common.badges.modified")}
        </span>
      )}
      {isNew && (
        <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded dark:text-blue-200 dark:bg-blue-900/60">
          {t("common.badges.new")}
        </span>
      )}
      <span className="inline-flex items-center ml-auto gap-1">
        <CommittedValidate committed={child.committed} />
        <WaitingValidate waiting={child.waiting} />
        {showReset && onResetChild && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onResetChild(child);
            }}
            className="ml-2 text-gray-400 hover:text-orange-400"
          >
            <FaArrowCircleLeft size={16} />
          </button>
        )}
        {onUnassignChild && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnassignChild(child);
            }}
            className="ml-2 text-gray-400 hover:text-red-600"
            title={t("classrooms.overview_management.remove_from_class_sub")}
          >
            <FaArrowRightFromBracket size={16} />
          </button>
        )}
      </span>
    </li>
  );
};

const DroppableClassroom = ({
  classroom,
  onDropChild,
  draft,
  assigned,
  waiting,
  onUnassignChild,
  onResetChild,
  childOriginalLocation,
  modifiedAssignments,
  draftAssignments,
  suspiciousChildIds,
  classrooms,
  selectedYearObj,
}) => {
  const { t, i18n: classroomI18n } = useTranslation();
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "CHILD",
      drop: (child) => onDropChild(child, classroom.id),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [classroom],
  );

  const draftIds = new Set((draft || []).map((c) => c.id));
  const combinedChildren = [
    ...(assigned || [])
      .filter(isActiveChild)
      .filter((child) => !draftIds.has(child.id)),
    ...(draft || []).filter(isActiveChild),
  ];
  const combinedChildrenSorted = React.useMemo(() => {
    return [...combinedChildren].sort((a, b) =>
      compareChildrenByExactBirthDate(a, b, classroomI18n?.language || "nl"),
    );
  }, [combinedChildren, classroomI18n?.language]);

  const waitingFiltered = (waiting || [])
    .filter(isActiveChild)
    .filter((child) => !combinedChildren.find((c) => c.id === child.id));

  const ageDistribution = React.useMemo(() => {
    if (classroom.classType !== "KINDERGARTEN" || !selectedYearObj) return null;
    const startDate = toDateSafe(selectedYearObj.startDate);
    if (!startDate) return null;
    const counts = { young: 0, middle: 0, old: 0 };
    combinedChildren.forEach((child) => {
      const reference = new Date(startDate.getFullYear(), 11, 31);
      const age = calculateAge(child.dateBirth, reference);
      if (age <= 4) {
        counts.young += 1;
      } else if (age === 5) {
        counts.middle += 1;
      } else if (age >= 6) {
        counts.old += 1;
      }
    });
    const total = counts.young + counts.middle + counts.old;
    if (!total) return null;
    return { counts, total };
  }, [classroom.classType, combinedChildren, selectedYearObj]);

  const ageColors = {
    young: "bg-emerald-500",
    middle: "bg-amber-500",
    old: "bg-purple-600",
  };
  const ageRowBackgrounds = {
    young: "bg-emerald-50 dark:bg-emerald-900/30",
    middle: "bg-amber-50 dark:bg-amber-900/30",
    old: "bg-purple-50 dark:bg-purple-900/30",
  };
  const ageSegments = ["young", "middle", "old"];
  const activeTeachers = Array.isArray(classroom.teachers)
    ? classroom.teachers.filter((teacher) => !teacher?.endDate)
    : [];
  const primaryTeacher = activeTeachers[0] || null;
  const primaryTeacherFirstName = primaryTeacher
    ? primaryTeacher.firstName ||
      (primaryTeacher.teacherName
        ? primaryTeacher.teacherName.split(" ")[0]
        : null) ||
      primaryTeacher.preferredName ||
      primaryTeacher.displayName ||
      null
    : null;

  return (
    <div
      ref={drop}
      className={`rounded-2xl border border-indigo-100/70 bg-white/95 p-4 shadow-sm ring-1 ring-transparent transition hover:-translate-y-0.5 hover:shadow-[0_20px_35px_rgba(15,23,42,0.12)] dark:border-gray-700/70 dark:bg-gray-900/80 ${
        isOver ? "ring-2 ring-emerald-400 dark:ring-emerald-500" : ""
      }`}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        {translateClassName(classroom)}
        {classroom.classType === "KINDERGARTEN" && (
          <span className="text-sm font-normal text-emerald-700 dark:text-emerald-300">
            ·{" "}
            {primaryTeacherFirstName ||
              t(
                "classrooms.overview_management.no_teacher_placeholder",
                "Geen leerkracht",
              )}
          </span>
        )}
        {(() => {
          const current = combinedChildren.length;
          const capacity =
            classroom.capacity ??
            classroom.maxChildren ??
            classroom.size ??
            null;
          const over = capacity != null && current > capacity;
          return (
            <span
              className={
                "text-sm px-2 py-0.5 rounded " +
                (over
                  ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200")
              }
              title="Bezetting / Capaciteit"
            >
              {current}/{capacity ?? "—"}
            </span>
          );
        })()}
      </h3>
      {ageDistribution && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            <span className="inline-flex items-center gap-2">
              {t("classrooms.kindergarten_age_distribution")}
              <button
                type="button"
                className="rounded bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                title={ageSegments
                  .map(
                    (key) =>
                      `${t(`classrooms.kindergarten_ages.${key}`)} · ${ageDistribution.counts[key]}`,
                  )
                  .join("\n")}
              >
                i
              </button>
            </span>
            <span>{ageDistribution.total}</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
            {ageSegments.map((key) => {
              const count = ageDistribution.counts[key];
              const percent =
                ageDistribution.total > 0
                  ? (count / ageDistribution.total) * 100
                  : 0;
              return (
                <div
                  key={key}
                  className={`${ageColors[key]} h-full`}
                  style={{
                    width: `${percent}%`,
                    minWidth: count > 0 ? "4px" : "0",
                  }}
                  title={`${t(`classrooms.kindergarten_ages.${key}`)} · ${count}`}
                />
              );
            })}
          </div>
        </div>
      )}
      {(() => {
        const maleCount = combinedChildren.filter(
          (child) => child.gender === "M",
        ).length;
        const femaleCount = combinedChildren.filter(
          (child) => child.gender === "F",
        ).length;
        const total = maleCount + femaleCount;
        if (!total) return null;
        const malePercent = (maleCount / total) * 100;
        const femalePercent = (femaleCount / total) * 100;
        return (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              <span>{t("classrooms.kindergarten_gender_distribution")}</span>
              <span>{total}</span>
            </div>
            <div className="flex h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
              <div
                className="bg-blue-500 h-full"
                style={{
                  width: `${malePercent}%`,
                  minWidth: maleCount > 0 ? "4px" : "0",
                }}
                title={`${t("classrooms.gender.male")}: ${maleCount}`}
              />
              <div
                className="bg-pink-400 h-full"
                style={{
                  width: `${femalePercent}%`,
                  minWidth: femaleCount > 0 ? "4px" : "0",
                }}
                title={`${t("classrooms.gender.female")}: ${femaleCount}`}
              />
            </div>
          </div>
        );
      })()}
      <ul className="mt-2 text-sm text-gray-700 dark:text-gray-300">
        {combinedChildrenSorted.map((child) => {
          const suggestedClassName = suggestClassroomForChild(
            child,
            classrooms,
            selectedYearObj,
          );
          const childAgeSegment = (() => {
            if (
              classroom.classType !== "KINDERGARTEN" ||
              !selectedYearObj?.startDate
            )
              return null;
            const startDate = toDateSafe(selectedYearObj.startDate);
            if (!startDate) return null;
            const reference = new Date(startDate.getFullYear(), 11, 31);
            const age = calculateAge(child.dateBirth, reference);
            if (age <= 4) return "young";
            if (age === 5) return "middle";
            if (age >= 6) return "old";
            return null;
          })();

          return (
            <DraggableChild
              key={child.id}
              child={child}
              suggestedClassName={suggestedClassName}
              onResetChild={onResetChild}
              onUnassignChild={onUnassignChild}
              childOriginalLocation={childOriginalLocation}
              showReset={
                childOriginalLocation[child.id]?.type !== "classroom" ||
                childOriginalLocation[child.id]?.classroomId !== classroom.id
              }
              isSuspicious={suspiciousChildIds.has(child.id)}
              modifiedAssignments={modifiedAssignments}
              draftAssignments={draftAssignments}
              schoolYear={selectedYearObj}
              rowToneClass={
                childAgeSegment ? ageRowBackgrounds[childAgeSegment] : ""
              }
            />
          );
        })}
      </ul>
    </div>
  );
};

const ClassroomOverview: React.FC = () => {
  const { keycloak } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const canManageOverview = useCan("school.classrooms.overview", "manage");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = canManageOverview || canCrudClassrooms;
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [unassignedChildren, setUnassignedChildren] = useState([]);
  const [draftAssignments, setDraftAssignments] = useState({});
  const [assignedChildren, setAssignedChildren] = useState({});
  const [initialAssignedChildren, setInitialAssignedChildren] = useState({});
  const [waitingListChildren, setWaitingListChildren] = useState({});
  const [initialUnassignedChildren, setInitialUnassignedChildren] = useState(
    [],
  );
  const [initialWaitingListChildren, setInitialWaitingListChildren] = useState(
    {},
  );
  const [childOriginalLocation, setChildOriginalLocation] = useState({});
  const [modal, setModal] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });
  const hasShownSuspiciousModal = useRef(false);
  const [showSuspiciousBanner, setShowSuspiciousBanner] = useState(false);
  const skipSuspiciousModalRef = useRef(false);
  const [modifiedAssignments, setModifiedAssignments] = useState([]);
  const [draftSummaries, setDraftSummaries] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);
  const [draftFormOpen, setDraftFormOpen] = useState(false);
  const [draftFormLabel, setDraftFormLabel] = useState("");
  const [draftFormNotes, setDraftFormNotes] = useState("");
  const [draftFormError, setDraftFormError] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftDetailState, setDraftDetailState] = useState({
    open: false,
    summary: null,
    detail: null,
    loading: false,
  });
  const [reloadCounter, setReloadCounter] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    label: null,
    draftId: null,
  });
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [loadingDraftId, setLoadingDraftId] = useState(null);
  const [applyingDraftId, setApplyingDraftId] = useState(null);
  const [suspiciousChildIds, setSuspiciousChildIds] = useState(new Set());
  const [appliedDraft, setAppliedDraft] = useState(null);
  const [showFlowPanel, setShowFlowPanel] = useState(false);
  const [nextYearClassrooms, setNextYearClassrooms] = useState([]);
  const [nextYearAssignedChildren, setNextYearAssignedChildren] = useState({});
  const [loadingFlow, setLoadingFlow] = useState(false);
  const selectedYearObj = React.useMemo(
    () => schoolYears.find((year) => year.id === selectedYear) || null,
    [schoolYears, selectedYear],
  );
  const selectedYearIsCurrent = React.useMemo(() => {
    const today = startOfDay(new Date());
    const start = startOfDay(selectedYearObj?.startDate);
    const end = startOfDay(selectedYearObj?.endDate);
    return !!start && !!end && today >= start && today <= end;
  }, [selectedYearObj]);
  const sortedSchoolYears = React.useMemo(() => {
    if (!Array.isArray(schoolYears)) return [];
    return [...schoolYears].sort((a, b) => {
      const aStart = toDateSafe(a?.startDate)?.getTime() ?? 0;
      const bStart = toDateSafe(b?.startDate)?.getTime() ?? 0;
      return aStart - bStart;
    });
  }, [schoolYears]);
  const currentYearOption = React.useMemo(() => {
    const today = new Date();
    return (
      sortedSchoolYears.find((year) => {
        const start = toDateSafe(year?.startDate);
        const end = toDateSafe(year?.endDate);
        return start && end && today >= start && today <= end;
      }) || null
    );
  }, [sortedSchoolYears]);
  const nextYearOption = React.useMemo(() => {
    if (!currentYearOption) return sortedSchoolYears[0] || null;
    const idx = sortedSchoolYears.findIndex(
      (year) => year.id === currentYearOption.id,
    );
    return idx >= 0 ? sortedSchoolYears[idx + 1] || null : null;
  }, [currentYearOption, sortedSchoolYears]);
  const nextYearObj = React.useMemo(() => {
    if (!selectedYearObj?.endDate) return null;
    const currentEnd = toDateSafe(selectedYearObj.endDate);
    if (!currentEnd) return null;
    const candidates = (schoolYears || [])
      .map((year) => ({
        year,
        start: toDateSafe(year?.startDate),
      }))
      .filter(({ start }) => !!start && start > currentEnd)
      .sort((a, b) => a.start - b.start);
    return candidates[0]?.year || null;
  }, [selectedYearObj, schoolYears]);
  const draftDetailCacheRef = useRef(new Map());
  const userDetailsCacheRef = useRef(new Map());
  const pendingUserRequestsRef = useRef(new Map());
  const hasLoadedNextYearRef = useRef(false);
  const currentLocale = i18n?.language || "nl-NL";
  const hasPendingChanges = React.useMemo(() => {
    const hasDraftData = Object.values(draftAssignments || {}).some(
      (list) => Array.isArray(list) && list.length > 0,
    );
    return hasDraftData || modifiedAssignments.length > 0;
  }, [draftAssignments, modifiedAssignments]);
  const formatDateTime = useCallback(
    (value) => {
      const date = parseToDate(value);
      return date ? date.toLocaleString(currentLocale) : "—";
    },
    [currentLocale],
  );
  const getCapacity = useCallback((cls) => {
    if (!cls || typeof cls !== "object") return null;
    return cls.capacity ?? cls.maxChildren ?? cls.size ?? null;
  }, []);
  const ageAt = useCallback((dateBirth, atDate) => {
    if (!dateBirth || !atDate) return null;
    const birth = toDateSafe(dateBirth);
    const ref = toDateSafe(atDate);
    if (!birth || !ref) return null;
    let age = ref.getFullYear() - birth.getFullYear();
    const monthDiff = ref.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age;
  }, []);
  const willGoToGrade1NextYear = useCallback(
    (child, classroom) => {
      if (!child || !classroom) return false;

      const birthDate = toDateSafe(child.dateBirth);
      if (!birthDate) return false;

      // Kleuterklas jaar 2 stroomt altijd door
      if (
        classroom.classType === "KINDERGARTEN" &&
        Number(classroom.grade) === 2
      )
        return true;

      const startOfNextYear = toDateSafe(nextYearObj?.startDate);
      if (!startOfNextYear) return false;

      const ageAtStart = ageAt(child.dateBirth, nextYearObj.startDate);
      if (ageAtStart != null && ageAtStart >= 7) {
        return true;
      }

      const endOfNextYear =
        toDateSafe(nextYearObj?.endDate) ??
        new Date(startOfNextYear.getFullYear(), 11, 31);

      const seventhBirthday = new Date(birthDate);
      seventhBirthday.setFullYear(seventhBirthday.getFullYear() + 7);

      if (
        seventhBirthday < startOfNextYear ||
        seventhBirthday > endOfNextYear
      ) {
        return false;
      }

      let easterDuringSchoolYear = calculateEasterSunday(
        startOfNextYear.getFullYear(),
      );
      if (easterDuringSchoolYear < startOfNextYear) {
        easterDuringSchoolYear = calculateEasterSunday(
          startOfNextYear.getFullYear() + 1,
        );
      }

      if (!easterDuringSchoolYear) return false;

      return seventhBirthday <= easterDuringSchoolYear;
    },
    [nextYearObj, ageAt],
  );
  const formatDraftChangeSummary = useCallback(
    (draft) => {
      if (!draft || typeof draft !== "object") return "";
      const additions = Number(
        draft.totalAdditions ??
          draft.additions ??
          draft.added ??
          draft.addCount,
      );
      const moves = Number(
        draft.totalMoves ?? draft.moves ?? draft.moved ?? draft.moveCount,
      );
      const removals = Number(
        draft.totalRemovals ??
          draft.removals ??
          draft.removed ??
          draft.removeCount,
      );
      const total =
        Number.isFinite(additions) ||
        Number.isFinite(moves) ||
        Number.isFinite(removals)
          ? (Number.isFinite(additions) ? additions : 0) +
            (Number.isFinite(moves) ? moves : 0) +
            (Number.isFinite(removals) ? removals : 0)
          : Number(draft.totalChanges ?? draft.changeCount ?? draft.changes);

      const parts = [];
      if (Number.isFinite(additions) && additions > 0) {
        parts.push(
          t("classrooms.overview_management.draft_summary_additions", {
            count: additions,
          }),
        );
      }
      if (Number.isFinite(moves) && moves > 0) {
        parts.push(
          t("classrooms.overview_management.draft_summary_moves", {
            count: moves,
          }),
        );
      }
      if (Number.isFinite(removals) && removals > 0) {
        parts.push(
          t("classrooms.overview_management.draft_summary_removals", {
            count: removals,
          }),
        );
      }
      if (parts.length === 0 && Number.isFinite(total) && total > 0) {
        parts.push(
          t("classrooms.overview_management.draft_summary_total", {
            count: total,
          }),
        );
      }
      return parts.join(" · ");
    },
    [t],
  );
  const childLookup = React.useMemo(() => {
    const map = new Map();
    const registerIdentifierVariants = (identifier, child) => {
      if (identifier == null) return;
      const variants = [];
      variants.push(identifier);
      const str = String(identifier).trim();
      if (str) {
        variants.push(str);
        const lower = str.toLowerCase();
        if (lower && lower !== str) variants.push(lower);
      }
      variants.forEach((variant) => {
        if (variant == null) return;
        if (!map.has(variant)) {
          map.set(variant, child);
        }
      });
    };
    const registerChild = (child) => {
      if (!child) return;
      const identifiers = [
        child.id,
        child.childId,
        child.childID,
        child.uuid,
        child.childUuid,
        child.childUUID,
      ];
      identifiers.forEach((identifier) =>
        registerIdentifierVariants(identifier, child),
      );
    };
    const registerList = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach(registerChild);
    };
    const registerMap = (collection) => {
      if (!collection || typeof collection !== "object") return;
      Object.values(collection).forEach(registerList);
    };
    const registerAssignments = (assignments) => {
      if (Array.isArray(assignments)) {
        assignments.forEach((assignment) => {
          if (assignment?.child) registerChild(assignment.child);
          const children = Array.isArray(assignment?.children)
            ? assignment.children
            : [];
          registerList(children);
        });
        return;
      }
      if (assignments && typeof assignments === "object") {
        Object.values(assignments).forEach((value) => {
          if (Array.isArray(value)) registerAssignments(value);
        });
      }
    };
    const registerChanges = (changes) => {
      if (!Array.isArray(changes)) return;
      changes.forEach((change) => {
        if (change?.child) registerChild(change.child);
      });
    };

    registerMap(initialAssignedChildren);
    registerMap(initialWaitingListChildren);
    registerList(initialUnassignedChildren);
    registerMap(assignedChildren);
    registerMap(waitingListChildren);
    registerList(unassignedChildren);
    registerMap(draftAssignments);

    const detail = draftDetailState.detail;
    const summary = draftDetailState.summary;
    if (detail && typeof detail === "object") {
      registerList(detail.children);
      registerList(detail.snapshotChildren);
      registerList(detail.baseline?.children);
      registerAssignments(detail.assignments);
      registerAssignments(detail.currentAssignments);
      registerAssignments(detail.proposedAssignments);
      registerChanges(detail.diff?.changes);
    }
    if (summary && typeof summary === "object") {
      registerList(summary.children);
      registerAssignments(summary.assignments);
      registerAssignments(summary.currentAssignments);
      registerAssignments(summary.proposedAssignments);
      registerChanges(summary.diff?.changes);
    }

    return map;
  }, [
    assignedChildren,
    draftAssignments,
    draftDetailState.detail,
    draftDetailState.summary,
    initialAssignedChildren,
    initialUnassignedChildren,
    initialWaitingListChildren,
    unassignedChildren,
    waitingListChildren,
  ]);
  const resolveChildIdentifier = useCallback(
    (identifier) => {
      if (identifier == null) return null;
      if (childLookup.has(identifier)) return childLookup.get(identifier);
      const str = String(identifier).trim();
      if (!str) return null;
      if (childLookup.has(str)) return childLookup.get(str);
      const lower = str.toLowerCase();
      if (childLookup.has(lower)) return childLookup.get(lower);
      return null;
    },
    [childLookup],
  );
  const resolveChildFromCandidates = useCallback(
    (candidates = []) => {
      for (const candidate of candidates) {
        const resolved = resolveChildIdentifier(candidate);
        if (resolved) return resolved;
      }
      return null;
    },
    [resolveChildIdentifier],
  );
  const registerUserInCache = useCallback((identifier, user) => {
    if (!identifier) return;
    const key = String(identifier).trim();
    if (!key) return;
    const value = user ?? null;
    if (!userDetailsCacheRef.current.has(key)) {
      userDetailsCacheRef.current.set(key, value);
    }
    const lower = key.toLowerCase();
    if (lower && !userDetailsCacheRef.current.has(lower)) {
      userDetailsCacheRef.current.set(lower, value);
    }
  }, []);
  const cacheUserRecord = useCallback(
    (user, identifiers = []) => {
      if (!user || typeof user !== "object") return;
      const collected = new Set(
        (Array.isArray(identifiers) ? identifiers : [identifiers])
          .map((value) => (value != null ? String(value).trim() : ""))
          .filter(Boolean),
      );
      [
        user.id,
        user.userId,
        user.userID,
        user.uuid,
        user.keycloakId,
        user.keycloakID,
        user.externalId,
        user.externalID,
        user.subject,
        user.sub,
      ]
        .map((value) => (value != null ? String(value).trim() : ""))
        .filter(Boolean)
        .forEach((value) => collected.add(value));

      collected.forEach((value) => registerUserInCache(value, user));
    },
    [registerUserInCache],
  );
  const loadUserDetails = useCallback(
    async (userIds = []) => {
      const unique = Array.from(
        new Set(
          (Array.isArray(userIds) ? userIds : [userIds])
            .map((id) => (id != null ? String(id).trim() : ""))
            .filter(Boolean)
            .filter((id) => looksLikeUuid(id)),
        ),
      );

      if (unique.length === 0) return new Map();

      const fetchPromises = unique
        .map((id) => {
          if (userDetailsCacheRef.current.has(id)) return null;
          if (pendingUserRequestsRef.current.has(id)) {
            return pendingUserRequestsRef.current.get(id);
          }

          const promise = (async () => {
            try {
              const { data } = await http.get(
                `/admin/users/${encodeURIComponent(id)}`,
              );
              const rawUser = data?.user ?? data ?? null;
              if (rawUser && typeof rawUser === "object") {
                cacheUserRecord(rawUser, [id]);
              } else {
                registerUserInCache(id, null);
              }
            } catch {
              registerUserInCache(id, null);
            } finally {
              pendingUserRequestsRef.current.delete(id);
            }
          })();

          pendingUserRequestsRef.current.set(id, promise);
          return promise;
        })
        .filter(Boolean);

      if (fetchPromises.length > 0) {
        await Promise.all(fetchPromises);
      }

      const result = new Map();
      unique.forEach((id) => {
        const cached = userDetailsCacheRef.current.get(id);
        if (cached) result.set(id, cached);
      });
      return result;
    },
    [cacheUserRecord, registerUserInCache],
  );
  const ensureUserDetails = useCallback(
    async (identifiers = []) => {
      const normalized = (
        Array.isArray(identifiers) ? identifiers : [identifiers]
      )
        .map((identifier) =>
          identifier != null ? String(identifier).trim() : "",
        )
        .filter(Boolean)
        .filter((identifier) => looksLikeUuid(identifier))
        .filter((identifier) => {
          if (userDetailsCacheRef.current.has(identifier)) return false;
          const lower = identifier.toLowerCase();
          return !userDetailsCacheRef.current.has(lower);
        });
      if (normalized.length === 0) return;
      await loadUserDetails(normalized);
    },
    [loadUserDetails],
  );
  const resolveUserIdentifier = useCallback((identifier) => {
    if (identifier == null) return null;
    if (userDetailsCacheRef.current.has(identifier))
      return userDetailsCacheRef.current.get(identifier);
    const str = String(identifier).trim();
    if (!str) return null;
    if (userDetailsCacheRef.current.has(str))
      return userDetailsCacheRef.current.get(str);
    const lower = str.toLowerCase();
    if (userDetailsCacheRef.current.has(lower))
      return userDetailsCacheRef.current.get(lower);
    return null;
  }, []);
  const resolveUserDisplayName = useCallback(
    (identifier) => {
      const user = resolveUserIdentifier(identifier);
      if (!user) return null;
      return getUserDisplayName(user);
    },
    [resolveUserIdentifier],
  );

  const applyDraftToWorkspace = useCallback(
    (detail, label) => {
      if (!detail || typeof detail !== "object") return false;

      const assignments = Array.isArray(detail.assignments)
        ? detail.assignments
        : [];
      const diffChangesRaw = Array.isArray(detail?.diff?.changes)
        ? detail.diff.changes
        : Array.isArray(detail?.changes)
          ? detail.changes
          : [];
      const diffChanges = diffChangesRaw.filter(Boolean);

      if (assignments.length === 0 && diffChanges.length === 0) {
        setModal({
          show: true,
          type: "info",
          title: t(
            "classrooms.overview_management.draft_apply_no_changes_title",
          ),
          message: t(
            "classrooms.overview_management.draft_apply_no_changes_message",
            { label },
          ),
        });
        return false;
      }

      const childIndex = new Map();
      const registerChild = (child) => {
        if (!child || child.id == null) return;
        if (!childIndex.has(child.id)) childIndex.set(child.id, child);
      };
      const registerList = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach(registerChild);
      };
      const registerMap = (map) => {
        if (!map || typeof map !== "object") return;
        Object.values(map).forEach(registerList);
      };

      registerMap(initialAssignedChildren);
      registerMap(initialWaitingListChildren);
      registerList(initialUnassignedChildren);
      registerMap(assignedChildren);
      registerMap(waitingListChildren);
      registerList(unassignedChildren);
      if (Array.isArray(detail.children)) registerList(detail.children);
      if (Array.isArray(detail.snapshotChildren))
        registerList(detail.snapshotChildren);
      if (Array.isArray(detail.baseline?.children))
        registerList(detail.baseline.children);
      diffChanges.forEach((change) => {
        if (change?.child) registerChild(change.child);
      });

      const ensureChild = (childId) => {
        if (childId == null) return null;
        let child = childIndex.get(childId);
        if (child) return child;
        const pools = [
          detail.children,
          detail.snapshotChildren,
          detail.baseline?.children,
        ];
        for (const pool of pools) {
          if (!Array.isArray(pool)) continue;
          const found = pool.find((entry) => entry?.id === childId);
          if (found) {
            registerChild(found);
            return found;
          }
        }
        return null;
      };

      const cloneChildren = (list = []) =>
        list
          .filter(isActiveChild)
          .map((child) => ensureChild(child.id) || child)
          .filter(Boolean);

      const normalizeLists = (map) => {
        Object.keys(map).forEach((key) => {
          map[key] = (map[key] || [])
            .filter(Boolean)
            .sort((a, b) =>
              compareChildrenByExactBirthDate(a, b, currentLocale),
            );
        });
      };

      const nextAssigned = {};
      classrooms.forEach((cls) => {
        nextAssigned[cls.id] = cloneChildren(assignedChildren[cls.id]);
      });
      const nextWaiting = {};
      Object.entries(waitingListChildren).forEach(([cid, list]) => {
        nextWaiting[cid] = cloneChildren(list);
      });
      let nextUnassigned = cloneChildren(unassignedChildren);

      const removeChildEverywhere = (childId) => {
        Object.keys(nextAssigned).forEach((cid) => {
          nextAssigned[cid] = nextAssigned[cid].filter(
            (child) => child?.id !== childId,
          );
        });
        Object.keys(nextWaiting).forEach((cid) => {
          nextWaiting[cid] = nextWaiting[cid].filter(
            (child) => child?.id !== childId,
          );
        });
        nextUnassigned = nextUnassigned.filter(
          (child) => child?.id !== childId,
        );
      };

      const modifiedSet = new Set();

      if (assignments.length > 0) {
        const desiredAssignments = new Map();
        assignments.forEach(({ classroomId, childIds }) => {
          if (!classroomId) return;
          (Array.isArray(childIds) ? childIds : []).forEach((childId) => {
            if (childId != null) desiredAssignments.set(childId, classroomId);
          });
        });

        desiredAssignments.forEach((classroomId, childId) => {
          const child = ensureChild(childId);
          if (!child) return;
          removeChildEverywhere(childId);
          const originalClassroom =
            childOriginalLocation[childId]?.classroomId ?? null;
          if (classroomId !== originalClassroom) {
            modifiedSet.add(childId);
          }
          if (classroomId && nextAssigned[classroomId]) {
            nextAssigned[classroomId].push(child);
          } else {
            nextUnassigned.push(child);
          }
        });

        const desiredIds = new Set(desiredAssignments.keys());
        nextUnassigned
          .filter((child) => !desiredIds.has(child.id))
          .forEach((child) => {
            const originalClassroom =
              childOriginalLocation[child.id]?.classroomId ?? null;
            if (originalClassroom != null) {
              modifiedSet.add(child.id);
            }
          });
      }

      if (diffChanges.length > 0) {
        const resolveTargetClassroomId = (change) => {
          const directCandidates = [
            change?.toClassroomId,
            change?.targetClassroomId,
            change?.afterClassroomId,
            change?.classroomId,
            change?.after?.classroomId,
            change?.after?.classroom?.id,
            change?.toClassroom?.id,
            change?.to?.classroomId,
            change?.to?.id,
            change?.targetClassroom?.id,
          ];

          for (const candidate of directCandidates) {
            if (candidate !== undefined && candidate !== null) return candidate;
          }

          const nameCandidates = [
            change?.toClassroomName,
            change?.after?.classroomName,
            change?.toClassroom?.name,
            change?.to?.name,
            change?.targetClassroom?.name,
          ].filter(Boolean);

          if (nameCandidates.length > 0) {
            const normalized = new Set(
              nameCandidates.map((name) => String(name).trim().toLowerCase()),
            );
            const match = classrooms.find((cls) =>
              normalized.has(
                String(cls.className || "")
                  .trim()
                  .toLowerCase(),
              ),
            );
            if (match) return match.id;
          }

          const gradeCandidate =
            change?.toClassroom?.grade ??
            change?.after?.classroom?.grade ??
            change?.targetClassroom?.grade ??
            change?.to?.grade ??
            null;
          if (gradeCandidate != null) {
            const match = classrooms.find(
              (cls) => cls.grade === gradeCandidate,
            );
            if (match) return match.id;
          }

          return null;
        };

        diffChanges.forEach((change) => {
          const type = String(
            change?.type || change?.changeType || "",
          ).toUpperCase();
          const childId =
            change?.childId ?? change?.child?.id ?? change?.childID ?? null;
          if (childId == null) return;
          const child = ensureChild(childId);
          if (!child) return;
          removeChildEverywhere(childId);

          if (type === "REMOVE") {
            nextUnassigned.push(child);
            modifiedSet.add(childId);
            return;
          }

          const toClassroomId = resolveTargetClassroomId(change);

          if (toClassroomId && nextAssigned[toClassroomId]) {
            nextAssigned[toClassroomId].push(child);
            modifiedSet.add(childId);
          } else {
            nextUnassigned.push(child);
            modifiedSet.add(childId);
          }
        });
      }

      normalizeLists(nextAssigned);
      normalizeLists(nextWaiting);
      nextUnassigned = nextUnassigned
        .filter(Boolean)
        .sort((a, b) => compareChildrenByExactBirthDate(a, b, currentLocale));

      if (modifiedSet.size === 0) {
        setModal({
          show: true,
          type: "info",
          title: t(
            "classrooms.overview_management.draft_apply_no_changes_title",
          ),
          message: t(
            "classrooms.overview_management.draft_apply_no_changes_message",
            { label },
          ),
        });
        return false;
      }

      setAssignedChildren(nextAssigned);
      setWaitingListChildren(nextWaiting);
      setUnassignedChildren(nextUnassigned);
      setDraftAssignments({});
      setModifiedAssignments(Array.from(modifiedSet));
      setShowSuspiciousBanner(false);
      setSuspiciousChildIds(new Set());
      hasShownSuspiciousModal.current = false;

      setModal({
        show: true,
        type: "success",
        title: t("classrooms.overview_management.draft_apply_success"),
        message: t(
          "classrooms.overview_management.draft_apply_success_message",
          { label },
        ),
      });

      return true;
    },
    [
      assignedChildren,
      classrooms,
      initialAssignedChildren,
      initialUnassignedChildren,
      initialWaitingListChildren,
      setAssignedChildren,
      currentLocale,
      setDraftAssignments,
      setModal,
      setModifiedAssignments,
      setShowSuspiciousBanner,
      setSuspiciousChildIds,
      setUnassignedChildren,
      setWaitingListChildren,
      t,
      unassignedChildren,
      waitingListChildren,
    ],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isMobileLayout, setIsMobileLayout] = useState(false);

  // Back to the previous Lumen page, or home; never out of Lumen.
  const handleMobileBack = useBackNavigation("/");

  // 🆕 Detecteer mobiele layout (ongeacht portrait/landscape)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLayout = () => {
      const width = window.innerWidth;
      // zelfde grens als Tailwind `lg` → alles daaronder beschouwen we als "mobiel"
      setIsMobileLayout(width < 1024);
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);
    window.addEventListener("orientationchange", updateLayout);

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("orientationchange", updateLayout);
    };
  }, []);

  useEffect(() => {
    const loadSchoolYears = async () => {
      try {
        if (!keycloak?.token) return;
        const years = await fetchSchoolYears(keycloak.token);
        setSchoolYears(years ?? []);
        setSelectedYear(null);
      } catch (err) {
        setError(mapHttpErrorToMessage(err, t));
      }
    };
    loadSchoolYears();
  }, [keycloak?.token]);

  useEffect(() => {
    if (!selectedYear || !keycloak?.token) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [classroomsRes, unassignedRes] = await Promise.all([
          http.get("/classrooms", { params: { year: selectedYear } }),
          http.get("/reports/unassigned-children", {
            params: { schoolYearId: selectedYear },
          }),
        ]);

        if (cancelled) return;

        const classrooms = classroomsRes?.data ?? [];
        const unassigned = (unassignedRes?.data ?? []).filter(isActiveChild);

        setClassrooms(classrooms);
        setUnassignedChildren(unassigned);
        setDraftAssignments({});
        setInitialUnassignedChildren(unassigned);

        const waitingPerClass = {};
        await Promise.all(
          classrooms.map(async (cls) => {
            const resWait = await http.get(
              `/classrooms/${cls.id}/waiting-list`,
            );
            waitingPerClass[cls.id] = (resWait?.data ?? []).filter(
              isActiveChild,
            );
          }),
        );
        if (cancelled) return;
        setWaitingListChildren(waitingPerClass);
        setInitialWaitingListChildren(waitingPerClass);

        const childrenPerClass = {};
        await Promise.all(
          classrooms.map(async (cls) => {
            const resKids = await http.get(`/classrooms/${cls.id}/children`);
            childrenPerClass[cls.id] = (resKids?.data ?? []).filter(
              isActiveChild,
            );
          }),
        );
        if (cancelled) return;
        setAssignedChildren(childrenPerClass);
        setInitialAssignedChildren(childrenPerClass);

        const childLoc = {};
        unassigned.forEach((child) => {
          childLoc[child.id] = { type: "unassigned" };
        });
        Object.entries(waitingPerClass).forEach(([classroomId, children]) => {
          children.forEach((child) => {
            childLoc[child.id] = { type: "waiting", classroomId };
          });
        });
        Object.entries(childrenPerClass).forEach(([classroomId, children]) => {
          children.forEach((child) => {
            childLoc[child.id] = { type: "classroom", classroomId };
          });
        });
        setChildOriginalLocation(childLoc);
        draftDetailCacheRef.current.clear();
      } catch (err) {
        if (!cancelled) setError(mapHttpErrorToMessage(err, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, keycloak?.token, reloadCounter]);

  const getDraftDetail = useCallback(async (id, { force = false } = {}) => {
    if (!id) return null;
    const cache = draftDetailCacheRef.current;
    if (!force && cache.has(id)) {
      return cache.get(id);
    }
    const { data } = await fetchClassroomDraftById(id);
    cache.set(id, data || null);
    return data || null;
  }, []);

  const loadDraftSummaries = useCallback(
    async ({ forceDetails = false } = {}) => {
      if (!selectedYear || !canManageClassrooms) {
        setDraftSummaries([]);
        setDraftsLoading(false);
        return;
      }

      setDraftsLoading(true);
      setDraftsError(null);

      try {
        if (forceDetails) {
          draftDetailCacheRef.current.clear();
        }

        const { data } = await listClassroomDrafts(selectedYear);
        const summaries = Array.isArray(data) ? data : [];

        const enriched = await Promise.all(
          summaries.map(async (summary) => {
            if (!summary?.id) return summary;
            try {
              const detail = await getDraftDetail(summary.id, {
                force: forceDetails,
              });
              if (!detail) return summary;
              const diffChanges = Array.isArray(detail?.diff?.changes)
                ? detail.diff.changes
                : Array.isArray(detail?.changes)
                  ? detail.changes
                  : [];
              const additions = diffChanges.filter(
                (change) =>
                  String(
                    change?.type || change?.changeType || "",
                  ).toUpperCase() === "ADD",
              ).length;
              const moves = diffChanges.filter(
                (change) =>
                  String(
                    change?.type || change?.changeType || "",
                  ).toUpperCase() === "MOVE",
              ).length;
              const removals = diffChanges.filter(
                (change) =>
                  String(
                    change?.type || change?.changeType || "",
                  ).toUpperCase() === "REMOVE",
              ).length;
              const stale =
                detail?.diff?.stale ?? detail?.stale ?? summary?.stale ?? false;
              return {
                ...summary,
                stale,
                totalAdditions: additions,
                totalMoves: moves,
                totalRemovals: removals,
                totalChanges: diffChanges.length,
              };
            } catch {
              return summary;
            }
          }),
        );
        const creatorCandidates = new Set();
        enriched.forEach((summary) => {
          if (!summary || typeof summary !== "object") return;
          if (summary.createdByName) return;
          const identifiers = [
            summary.createdBy,
            summary.createdById,
            summary.createdByID,
            summary.createdByUuid,
            summary.createdByUUID,
            summary.author,
            summary.authorId,
          ]
            .map((value) => (value != null ? String(value).trim() : ""))
            .filter(Boolean);
          const uuidIdentifiers = identifiers.filter((identifier) =>
            looksLikeUuid(identifier),
          );
          uuidIdentifiers.forEach((identifier) => {
            const lower = identifier.toLowerCase();
            if (
              !userDetailsCacheRef.current.has(identifier) &&
              !userDetailsCacheRef.current.has(lower)
            ) {
              creatorCandidates.add(identifier);
            }
          });
        });

        if (creatorCandidates.size > 0) {
          await ensureUserDetails(Array.from(creatorCandidates));
        }

        const finalSummaries = enriched.map((summary) => {
          if (!summary || typeof summary !== "object") return summary;
          const identifiers = [
            summary.createdBy,
            summary.createdById,
            summary.createdByID,
            summary.createdByUuid,
            summary.createdByUUID,
            summary.author,
            summary.authorId,
          ];
          const resolvedName =
            summary.createdByName ||
            summary.createdByDisplay ||
            resolveUserDisplayName(identifiers.find((id) => id != null));

          return {
            ...summary,
            createdByDisplay:
              resolvedName ||
              summary.createdByName ||
              summary.createdBy ||
              summary.author ||
              "",
          };
        });

        setDraftSummaries(finalSummaries);
      } catch (err) {
        setDraftSummaries([]);
        setDraftsError(mapHttpErrorToMessage(err, t));
      } finally {
        setDraftsLoading(false);
      }
    },
    [
      selectedYear,
      canManageClassrooms,
      t,
      getDraftDetail,
      ensureUserDetails,
      resolveUserDisplayName,
    ],
  );

  useEffect(() => {
    loadDraftSummaries({ forceDetails: true });
  }, [loadDraftSummaries]);

  // 🔁 Reset de modal-flag zodra een nieuw jaar gekozen wordt
  useEffect(() => {
    hasShownSuspiciousModal.current = false;
  }, [selectedYear]);

  // 🔁 Reset suspicious IDs zodra data is geladen (optioneel, afhankelijk van timing)
  useEffect(() => {
    setSuspiciousChildIds(new Set());
    setAppliedDraft(null);
  }, [selectedYear]);

  const handleDropChild = (child, droppedClassroomId) => {
    const droppedClassroom = classrooms.find(
      (c) => c.id === droppedClassroomId,
    );
    if (!droppedClassroom) return;

    let classroomIdToAssign = droppedClassroomId;

    // 🔁 Bepaal grade als het om een kleuterklas gaat
    let kindergartenGrade = null;
    if (
      droppedClassroom.classType === "KINDERGARTEN" &&
      selectedYearObj?.startDate
    ) {
      kindergartenGrade = getKindergartenYearNumber(
        child.dateBirth,
        selectedYearObj.startDate,
      );
    }

    const original = childOriginalLocation[child.id];
    const originalClassroomId =
      original?.type === "classroom" ? original.classroomId : null;

    if (
      originalClassroomId !== classroomIdToAssign ||
      (original?.type && original?.type !== "classroom")
    ) {
      setModifiedAssignments((prev) => [...new Set([...prev, child.id])]);
    }

    // ✅ Draft aanpassen met extra prop
    setDraftAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((cid) => {
        updated[cid] = updated[cid].filter((c) => c.id !== child.id);
      });
      if (!updated[classroomIdToAssign]) updated[classroomIdToAssign] = [];

      updated[classroomIdToAssign].push({
        ...child,
        kindergartenGrade, // dit komt straks beschikbaar bij handleConfirmAssignments
      });

      return updated;
    });

    setAssignedChildren((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((cid) => {
        const list = Array.isArray(updated[cid]) ? updated[cid] : [];
        updated[cid] = list.filter((c) => c.id !== child.id);
      });
      return updated;
    });

    setUnassignedChildren((prev) => prev.filter((c) => c.id !== child.id));

    setWaitingListChildren((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([cid, list]) => {
        updated[cid] = (Array.isArray(list) ? list : []).filter(
          (c) => c.id !== child.id,
        );
      });
      return updated;
    });
  };

  const handleResetChild = (child) => {
    const original = childOriginalLocation[child.id];
    setModifiedAssignments((prev) => prev.filter((id) => id !== child.id)); // ✅ verwijder uit modifiedAssignments

    if (!original) return;

    // Verwijder uit alle plekken
    setDraftAssignments((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([cid, list]) => {
        updated[cid] = list.filter((c) => c.id !== child.id);
      });
      return updated;
    });
    setAssignedChildren((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([cid, list]) => {
        updated[cid] = list.filter((c) => c.id !== child.id);
      });
      return updated;
    });
    setUnassignedChildren((prev) => prev.filter((c) => c.id !== child.id));
    setWaitingListChildren((prev) => {
      const updated = {};
      Object.entries(prev).forEach(([cid, list]) => {
        updated[cid] = list.filter((c) => c.id !== child.id);
      });
      return updated;
    });

    // Voeg terug op oorspronkelijke plek
    if (original.type === "unassigned") {
      setUnassignedChildren((prev) => [...prev, child]);
    } else if (original.type === "waiting") {
      setWaitingListChildren((prev) => {
        const updated = { ...prev };
        if (!updated[original.classroomId]) updated[original.classroomId] = [];
        updated[original.classroomId].push(child);
        return updated;
      });
    } else if (original.type === "classroom") {
      setAssignedChildren((prev) => {
        const updated = { ...prev };
        if (!updated[original.classroomId]) updated[original.classroomId] = [];
        updated[original.classroomId].push(child);
        return updated;
      });
    }
  };

  const resetAssignments = () => {
    setDraftAssignments({});
    setAssignedChildren(initialAssignedChildren);
    setUnassignedChildren(initialUnassignedChildren);
    setWaitingListChildren(initialWaitingListChildren);
    setModifiedAssignments([]); // ✅ reset modified labels
    setAppliedDraft(null);

    // Herstel ongeplaatste kinderen (die niet in originele klasindeling zaten)
    const allDrafted = Object.values(draftAssignments).flat();
    const toRestore = allDrafted.filter((child) => {
      const original = childOriginalLocation[child.id];
      return original?.type === "unassigned";
    });
    setUnassignedChildren((prev) => [...prev, ...toRestore]);
  };

  const openDraftForm = () => {
    setDraftFormLabel("");
    setDraftFormNotes("");
    setDraftFormError(null);
    setDraftFormOpen(true);
  };

  const closeDraftForm = () => {
    if (isSavingDraft) return;
    setDraftFormOpen(false);
    setDraftFormError(null);
  };

  const handleSaveDraft = async (event) => {
    event.preventDefault();
    if (!selectedYear) return;

    const trimmedLabel = draftFormLabel.trim();
    if (!trimmedLabel) {
      setDraftFormError(
        t("classrooms.overview_management.draft_name_required"),
      );
      return;
    }

    const finalClassByChild = new Map();

    Object.entries(initialAssignedChildren || {}).forEach(
      ([classroomId, children]) => {
        (children || []).forEach((child) => {
          if (!isActiveChild(child)) return;
          if (child?.id == null) return;
          finalClassByChild.set(child.id, classroomId);
        });
      },
    );

    Object.entries(draftAssignments || {}).forEach(
      ([classroomId, children]) => {
        (children || []).forEach((child) => {
          if (!isActiveChild(child)) return;
          if (child?.id == null) return;
          finalClassByChild.set(child.id, classroomId);
        });
      },
    );

    const assignmentsByClassroom = new Map();
    classrooms.forEach((cls) => {
      assignmentsByClassroom.set(cls.id, []);
    });

    finalClassByChild.forEach((classroomId, childId) => {
      if (!classroomId) return;
      if (!assignmentsByClassroom.has(classroomId)) return;
      assignmentsByClassroom.get(classroomId).push(childId);
    });

    const payloadAssignments = classrooms.map((cls) => {
      const ids = assignmentsByClassroom.get(cls.id) || [];
      const uniqueChildIds = Array.from(new Set(ids)).sort((a, b) => {
        const an = Number(a);
        const bn = Number(b);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return String(a).localeCompare(String(b));
      });
      return {
        classroomId: cls.id,
        childIds: uniqueChildIds,
      };
    });

    const payload = {
      schoolYearId: selectedYear,
      label: trimmedLabel,
      notes: draftFormNotes.trim() || null,
      assignments: payloadAssignments,
    };

    setIsSavingDraft(true);
    setDraftFormError(null);

    try {
      const { data: createResponse } = await createClassroomDraft(payload);
      if (createResponse?.id) {
        draftDetailCacheRef.current.set(createResponse.id, createResponse);
      }
      setModal({
        show: true,
        type: "success",
        title: t("classrooms.overview_management.draft_save_success"),
        message: t(
          "classrooms.overview_management.draft_save_success_message",
          { label: trimmedLabel },
        ),
      });
      setDraftFormOpen(false);
      setDraftFormLabel("");
      setDraftFormNotes("");
      await loadDraftSummaries({ forceDetails: true });
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setDraftFormError(mapped.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleRefreshDrafts = () => {
    if (draftsLoading) return;
    loadDraftSummaries({ forceDetails: true });
  };

  const closeDraftDetail = useCallback(() => {
    setDraftDetailState({
      open: false,
      summary: null,
      detail: null,
      loading: false,
    });
  }, []);

  const deleteConfirmState = useRef({ currentDraft: null });

  const openDraftDeleteModal = useCallback(
    (draft) => {
      if (!draft || !draft.id) return;
      const label =
        draft.label ||
        draft.name ||
        draft.title ||
        t("classrooms.overview_management.draft_fallback_label", {
          id: draft.id,
        });

      deleteConfirmState.currentDraft = draft;
      setDeleteConfirm({
        show: true,
        label,
        draftId: draft.id,
      });
    },
    [t],
  );

  const closeDraftDeleteModal = useCallback(() => {
    setDeleteConfirm({ show: false, label: null, draftId: null });
    deleteConfirmState.currentDraft = null;
  }, []);

  const executeDeleteDraft = async (draft) => {
    if (!draft?.id) return;
    const label =
      draft.label ||
      draft.name ||
      draft.title ||
      t("classrooms.overview_management.draft_fallback_label", {
        id: draft.id,
      });

    setDeletingDraftId(draft.id);
    try {
      await deleteClassroomDraft(draft.id);
      draftDetailCacheRef.current.delete(draft.id);
      setModal({
        show: true,
        type: "success",
        title: t("classrooms.overview_management.draft_delete_success"),
        message: t(
          "classrooms.overview_management.draft_delete_success_message",
          { label },
        ),
      });

      if (draftDetailState.open && draftDetailState.summary?.id === draft.id) {
        closeDraftDetail();
      }

      await loadDraftSummaries({ forceDetails: true });
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setModal({
        show: true,
        type: "error",
        title: mapped.title,
        message: mapped.message,
        status: mapped.status,
      });
    } finally {
      setDeletingDraftId(null);
      closeDraftDeleteModal();
    }
  };

  const handleViewDraft = async (summary) => {
    if (!summary?.id) return;
    setLoadingDraftId(summary.id);
    setDraftDetailState({
      open: true,
      summary,
      detail: null,
      loading: true,
    });
    try {
      const detail = await getDraftDetail(summary.id, { force: true });
      const mergedDetail = detail ? { ...detail } : null;
      const mergedSummary = summary ? { ...summary } : {};

      const creatorIdentifiers = [
        mergedDetail?.createdBy,
        mergedDetail?.createdById,
        mergedDetail?.createdByID,
        mergedDetail?.createdByUuid,
        mergedDetail?.createdByUUID,
        mergedSummary?.createdBy,
        mergedSummary?.createdById,
        mergedSummary?.createdByID,
        mergedSummary?.createdByUuid,
        mergedSummary?.createdByUUID,
        mergedDetail?.author,
        mergedSummary?.author,
      ].filter((value) => value != null);

      if (creatorIdentifiers.length > 0) {
        await ensureUserDetails(creatorIdentifiers);
      }
      const primaryCreatorIdentifier =
        creatorIdentifiers.find(
          (value) => typeof value === "string" && looksLikeUuid(value),
        ) ?? creatorIdentifiers[0];
      const resolvedCreatorName =
        mergedDetail?.createdByName ||
        mergedSummary?.createdByName ||
        resolveUserDisplayName(primaryCreatorIdentifier);

      if (mergedDetail) {
        if (resolvedCreatorName && !mergedDetail.createdByName) {
          mergedDetail.createdByName = resolvedCreatorName;
        }
        mergedDetail.createdByDisplay =
          mergedDetail.createdByName ||
          resolveUserDisplayName(primaryCreatorIdentifier) ||
          mergedDetail.createdBy ||
          mergedDetail.createdById ||
          mergedDetail.author ||
          "";
      }
      if (mergedSummary && typeof mergedSummary === "object") {
        if (resolvedCreatorName && !mergedSummary.createdByName) {
          mergedSummary.createdByName = resolvedCreatorName;
        }
        mergedSummary.createdByDisplay =
          mergedSummary.createdByName ||
          resolveUserDisplayName(primaryCreatorIdentifier) ||
          mergedSummary.createdBy ||
          mergedSummary.createdById ||
          mergedSummary.author ||
          "";
      }

      setDraftDetailState({
        open: true,
        summary: mergedSummary,
        detail: mergedDetail,
        loading: false,
      });
      if (mergedDetail) {
        const stale = mergedDetail?.diff?.stale ?? mergedDetail?.stale ?? false;
        setDraftSummaries((prev) =>
          Array.isArray(prev)
            ? prev.map((item) =>
                item?.id === summary.id
                  ? {
                      ...item,
                      stale,
                      createdByDisplay:
                        mergedSummary?.createdByDisplay ||
                        mergedSummary?.createdByName ||
                        item?.createdByDisplay ||
                        item?.createdByName ||
                        item?.createdBy ||
                        item?.author ||
                        "",
                    }
                  : item,
              )
            : prev,
        );
      }
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setDraftDetailState({
        open: false,
        summary: null,
        detail: null,
        loading: false,
      });
      setModal({
        show: true,
        type: "error",
        title: mapped.title,
        message: mapped.message,
        status: mapped.status,
      });
    } finally {
      setLoadingDraftId(null);
    }
  };

  const handleApplyDraft = useCallback(
    async (draft) => {
      if (!draft?.id) return;
      const label =
        draft.label ||
        draft.name ||
        t("classrooms.overview_management.draft_fallback_label", {
          id: draft.id,
        });

      setApplyingDraftId(draft.id);
      try {
        const detail = await getDraftDetail(draft.id, { force: true });
        draftDetailCacheRef.current.set(draft.id, detail);

        const applied = applyDraftToWorkspace(detail, label);
        if (applied) {
          setAppliedDraft({ id: draft.id, label });
          setDraftSummaries((prev) =>
            Array.isArray(prev)
              ? prev.map((item) =>
                  item?.id === draft.id
                    ? {
                        ...item,
                        stale:
                          detail?.diff?.stale ??
                          detail?.stale ??
                          item?.stale ??
                          false,
                      }
                    : item,
                )
              : prev,
          );

          if (
            draftDetailState.open &&
            draftDetailState.summary?.id === draft.id
          ) {
            setDraftDetailState({
              open: false,
              summary: null,
              detail: null,
              loading: false,
            });
          }
        }
      } catch (err) {
        const mapped = mapHttpErrorToMessage(err, t);
        setModal({
          show: true,
          type: "error",
          title: mapped.title,
          message: mapped.message,
          status: mapped.status,
        });
      } finally {
        setApplyingDraftId(null);
      }
    },
    [
      applyDraftToWorkspace,
      getDraftDetail,
      setModal,
      setDraftSummaries,
      draftDetailState,
      t,
    ],
  );

  const handleConfirmAssignments = async () => {
    if (!canManageClassrooms) {
      setModal({
        show: true,
        type: "error",
        title: t("error.error_title2"),
        message: t(
          "classrooms.overview_management.save_forbidden",
          "You do not have permission to save these changes.",
        ),
      });
      return;
    }
    const finalClassByChild = new Map();
    const finalChildLookup = new Map();
    Object.entries(assignedChildren || {}).forEach(([classroomId, list]) => {
      (Array.isArray(list) ? list : []).forEach((child) => {
        if (!child || child.id == null) return;
        finalClassByChild.set(child.id, classroomId);
        finalChildLookup.set(child.id, { child, classroomId });
      });
    });
    Object.entries(draftAssignments || {}).forEach(([classroomId, list]) => {
      (Array.isArray(list) ? list : []).forEach((child) => {
        if (!child || child.id == null) return;
        finalClassByChild.set(child.id, classroomId);
        finalChildLookup.set(child.id, { child, classroomId });
      });
    });

    const promises = [];
    modifiedAssignments.forEach((childId) => {
      const targetClassroomId = finalClassByChild.get(childId);
      if (!targetClassroomId) return;
      const classroom = classrooms.find((c) => c.id === targetClassroomId);
      const lookup = finalChildLookup.get(childId);
      const childForGrade = lookup?.child;
      const grade = childForGrade?.kindergartenGrade ?? classroom?.grade;
      const persistAssignment = childForGrade?.waiting
        ? placeChildFromWaitingList
        : assignChildToClassroom;

      promises.push(
        persistAssignment(keycloak.token, childId, targetClassroomId, {
          grade,
          setCurrent: selectedYearIsCurrent,
        }),
      );
    });

    try {
      skipSuspiciousModalRef.current = true; // ❗️zet flag

      for (const childId of modifiedAssignments) {
        const original = childOriginalLocation[childId];
        if (original?.classroomId) {
          await unassignChildFromBackend(childId, original.classroomId);
        }
      }

      await Promise.all(promises);

      let deleteDraftError = null;
      let draftDeleted = false;
      if (appliedDraft?.id) {
        try {
          await deleteClassroomDraft(appliedDraft.id);
          draftDetailCacheRef.current.delete(appliedDraft.id);
          draftDeleted = true;
        } catch (deleteErr) {
          if (deleteErr?.response?.status === 404) {
            draftDeleted = true;
          } else {
            const mapped = mapHttpErrorToMessage(deleteErr, t);
            deleteDraftError =
              mapped?.message ||
              (appliedDraft?.label
                ? t(
                    "classrooms.overview_management.draft_delete_after_confirm_failed_with_label",
                    {
                      label: appliedDraft.label,
                      defaultValue: `Concept "${appliedDraft.label}" kon niet automatisch verwijderd worden. Verwijder het handmatig.`,
                    },
                  )
                : t(
                    "classrooms.overview_management.draft_delete_after_confirm_failed",
                    "Het toepaste concept kon niet automatisch verwijderd worden. Verwijder het handmatig.",
                  ));
          }
        }
      }

      setModal({
        show: true,
        type: deleteDraftError ? "warning" : "success",
        title: t("classrooms.overview_management.title"),
        message: deleteDraftError
          ? `${t("classrooms.overview_management.assignments_confirmed")}\n\n${deleteDraftError}`
          : t("classrooms.overview_management.assignments_confirmed"),
      });

      setDraftAssignments({});
      setModifiedAssignments([]);
      if (draftDeleted) {
        setAppliedDraft(null);
      }
      skipSuspiciousModalRef.current = false;
      hasShownSuspiciousModal.current = false;
      setSuspiciousChildIds(new Set());
      setLoading(true);
      setReloadCounter((value) => value + 1);
      await loadDraftSummaries({ forceDetails: true });
    } catch (err) {
      setModal({
        show: true,
        type: "error",
        title: t("error.error_title2"),
        status: err?.response?.status,
        message:
          err?.response?.status === 403
            ? t(
                "classrooms.overview_management.save_forbidden",
                "You do not have permission to save these changes.",
              )
            : t("common.save_error"),
      });
      skipSuspiciousModalRef.current = false;
    } finally {
      skipSuspiciousModalRef.current = false;
    }
  };

  const sortedClassrooms = [...classrooms].sort((a, b) => {
    // 1. Sorteer op classType
    if (a.classType !== b.classType) {
      return a.classType === "KINDERGARTEN" ? -1 : 1;
    }

    // 2. Sorteer op grade indien beschikbaar
    if (a.grade !== undefined && b.grade !== undefined) {
      return a.grade - b.grade;
    }

    // 3. Fallback naar className alfabetisch
    return a.className.localeCompare(b.className);
  });
  const flowNameCollator = React.useMemo(
    () => new Intl.Collator(i18n?.language || "nl", { sensitivity: "base" }),
    [i18n?.language],
  );
  const flowCandidates = React.useMemo(() => {
    if (!nextYearObj) return [];
    return sortedClassrooms
      .filter((classroom) => classroom.classType === "KINDERGARTEN")
      .flatMap((classroom) => {
        const current = (assignedChildren[classroom.id] || []).filter(
          isActiveChild,
        );
        return current
          .filter((child) => willGoToGrade1NextYear(child, classroom))
          .map((child) => ({ child, from: classroom }));
      })
      .sort((a, b) =>
        flowNameCollator.compare(
          buildChildLabel(a.child),
          buildChildLabel(b.child),
        ),
      );
  }, [
    sortedClassrooms,
    assignedChildren,
    nextYearObj,
    willGoToGrade1NextYear,
    flowNameCollator,
  ]);
  const totalAssignedNextYear = React.useMemo(() => {
    return nextYearClassrooms.reduce((sum, classroom) => {
      const list = nextYearAssignedChildren[classroom.id] || [];
      return sum + list.length;
    }, 0);
  }, [nextYearClassrooms, nextYearAssignedChildren]);
  const totalCapacityNextYear = React.useMemo(() => {
    if (!nextYearClassrooms.length) return null;
    const capacities = nextYearClassrooms.map((cls) => getCapacity(cls));
    if (capacities.some((cap) => cap == null)) return null;
    return capacities.reduce((sum, cap) => sum + cap, 0);
  }, [nextYearClassrooms, getCapacity]);
  const flowPreviewAssignments = React.useMemo(() => {
    if (!flowCandidates.length) return [];
    if (!nextYearClassrooms.length) {
      return flowCandidates.map(({ child, from }) => ({
        child,
        from,
        target: null,
        capacity: null,
        assignedAfter: null,
      }));
    }
    const occupancy = nextYearClassrooms.map((cls) => ({
      cls,
      capacity: getCapacity(cls),
      assigned: (nextYearAssignedChildren[cls.id] || []).length,
    }));

    return flowCandidates.map(({ child, from }) => {
      if (!occupancy.length) {
        return {
          child,
          from,
          target: null,
          capacity: null,
          assignedAfter: null,
        };
      }
      let bestIndex = -1;
      let bestAvailable = Number.NEGATIVE_INFINITY;
      occupancy.forEach((entry, index) => {
        const available =
          entry.capacity != null
            ? entry.capacity - entry.assigned
            : Number.NEGATIVE_INFINITY;
        if (available > bestAvailable) {
          bestAvailable = available;
          bestIndex = index;
        }
      });
      if (bestIndex === -1) {
        bestIndex = 0;
      }
      const entry = occupancy[bestIndex];
      const targetCls = entry.cls;
      entry.assigned += 1;
      return {
        child,
        from,
        target: targetCls,
        capacity: entry.capacity,
        assignedAfter: entry.capacity != null ? entry.assigned : null,
      };
    });
  }, [
    flowCandidates,
    nextYearClassrooms,
    nextYearAssignedChildren,
    getCapacity,
  ]);

  useEffect(() => {
    hasLoadedNextYearRef.current = false;
    setNextYearClassrooms([]);
    setNextYearAssignedChildren({});
  }, [nextYearObj?.id]);

  useEffect(() => {
    if (!nextYearObj?.id || !keycloak?.token) return;
    if (!showFlowPanel && hasLoadedNextYearRef.current) return;
    let cancelled = false;

    const loadNextYearClasses = async () => {
      try {
        setLoadingFlow(true);
        const { data } = await http.get("/classrooms", {
          params: { year: nextYearObj.id },
        });
        if (cancelled) return;
        const rooms = (data ?? []).filter(
          (cls) => cls.classType === "GRADE" && Number(cls.grade) === 1,
        );
        setNextYearClassrooms(rooms);

        const map = {};
        await Promise.all(
          rooms.map(async (room) => {
            try {
              const res = await http.get(
                `/child-classrooms/children-in-classroom/${room.id}/school-year/${nextYearObj.id}`,
              );
              map[room.id] = (res?.data ?? []).filter(isActiveChild);
            } catch (error) {
              map[room.id] = [];
            }
          }),
        );
        if (!cancelled) {
          setNextYearAssignedChildren(map);
          hasLoadedNextYearRef.current = true;
        }
      } catch (error) {
        if (!cancelled) {
          setNextYearClassrooms([]);
          setNextYearAssignedChildren({});
          hasLoadedNextYearRef.current = true;
        }
      } finally {
        if (!cancelled) {
          setLoadingFlow(false);
        }
      }
    };

    loadNextYearClasses();
    return () => {
      cancelled = true;
    };
  }, [showFlowPanel, nextYearObj?.id, keycloak?.token]);

  useEffect(() => {
    const groupMap = {};
    const assignedCount = {};

    const addGroup = (childId, group) => {
      if (childId == null) return;
      if (!groupMap[childId]) groupMap[childId] = new Set();
      groupMap[childId].add(group);
    };

    Object.entries(assignedChildren).forEach(([_, children]) => {
      if (!Array.isArray(children)) return;
      children.forEach((child) => {
        if (!child || child.id == null) return;
        addGroup(child.id, "assigned");
        assignedCount[child.id] = (assignedCount[child.id] || 0) + 1;
      });
    });

    Object.entries(draftAssignments).forEach(([_, children]) => {
      if (!Array.isArray(children)) return;
      children.forEach((child) => {
        if (!child || child.id == null) return;
        addGroup(child.id, "draft");
      });
    });

    Object.entries(waitingListChildren).forEach(([_, children]) => {
      if (!Array.isArray(children)) return;
      children.forEach((child) => {
        if (!child || child.id == null) return;
        addGroup(child.id, "waiting");
      });
    });

    unassignedChildren.forEach((child) => {
      if (!child || child.id == null) return;
      addGroup(child.id, "unassigned");
    });

    const suspicious = new Set(
      Object.keys(groupMap).filter((childId) => {
        const groupCount = groupMap[childId].size;
        const inMultipleGroups = groupCount > 1;
        const inMultipleAssigned = (assignedCount[childId] || 0) > 1;
        return inMultipleGroups || inMultipleAssigned;
      }),
    );

    const normalizedSuspicious = new Set();
    suspicious.forEach((id) => {
      normalizedSuspicious.add(id);
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) {
        normalizedSuspicious.add(numericId);
      }
    });
    setSuspiciousChildIds(normalizedSuspicious);

    if (
      suspicious.size > 0 &&
      !hasShownSuspiciousModal.current &&
      !skipSuspiciousModalRef.current
    ) {
      setModal({
        show: true,
        type: "warning",
        title: t("classrooms.overview_management.suspicious_detected_title"),
        message: t(
          "classrooms.overview_management.suspicious_detected_message",
        ),
      });
      hasShownSuspiciousModal.current = true;
    }
  }, [
    assignedChildren,
    draftAssignments,
    waitingListChildren,
    unassignedChildren,
    t,
  ]);

  const unassignChildFromBackend = async (childId, classroomId) => {
    await http.post(
      "/child-classrooms/unassign",
      null,
      { params: { childId, classroomId } }, // token wordt via interceptor gezet
    );
  };

  const handleUnassignChild = async (child, classroomId) => {
    try {
      await unassignChildFromBackend(child.id, classroomId);

      setDraftAssignments((prev) => {
        const updated = { ...prev };
        Object.entries(updated).forEach(([cid, list]) => {
          updated[cid] = list.filter((c) => c.id !== child.id);
        });
        return updated;
      });

      setAssignedChildren((prev) => {
        const updated = { ...prev };
        Object.entries(updated).forEach(([cid, list]) => {
          updated[cid] = list.filter((c) => c.id !== child.id);
        });
        return updated;
      });

      setUnassignedChildren((prev) => [...prev, child]);
    } catch (error) {
      setModal({
        show: true,
        type: "error",
        title: t("error.error_title2"),
        message: t("classrooms.details.unassign_failed"),
      });
    }
  };

  const hasAnyClassrooms = sortedClassrooms.length > 0;
  const kindergartenClassrooms = React.useMemo(
    () =>
      sortedClassrooms.filter(
        (classroom) => classroom.classType === "KINDERGARTEN",
      ),
    [sortedClassrooms],
  );
  const gradeClassrooms = React.useMemo(
    () =>
      sortedClassrooms.filter((classroom) => classroom.classType === "GRADE"),
    [sortedClassrooms],
  );
  const sortedUnassignedChildren = React.useMemo(
    () =>
      [...unassignedChildren]
        .filter(isActiveChild)
        .filter(
          (child, index, self) =>
            self.findIndex((candidate) => candidate.id === child.id) === index,
        )
        .sort((a, b) =>
          compareChildrenByExactBirthDate(a, b, currentLocale),
        ),
    [unassignedChildren, currentLocale],
  );
  const ageSortReferenceDate = React.useMemo(
    () => selectedYearObj?.startDate ?? new Date(),
    [selectedYearObj?.startDate],
  );
  const totalWaitingChildren = React.useMemo(() => {
    const seen = new Set();
    return Object.values(waitingListChildren)
      .flat()
      .filter(isActiveChild)
      .filter((child) => {
        if (seen.has(child.id)) return false;
        seen.add(child.id);

        const ageAtStart = calculateAge(
          child.dateBirth,
          ageSortReferenceDate,
        );
        const birthYear = getYearSafe(child.dateBirth);
        const schoolYearStart = getYearSafe(selectedYearObj?.startDate);
        const turnsFourThisYear =
          birthYear != null &&
          schoolYearStart != null &&
          schoolYearStart - birthYear === 4;

        return ageAtStart >= 4 || turnsFourThisYear;
      })
      .sort((a, b) =>
        compareChildrenByExactBirthDate(a, b, currentLocale),
      );
  }, [
    waitingListChildren,
    ageSortReferenceDate,
    selectedYearObj?.startDate,
    currentLocale,
  ]);

  const printClassrooms = React.useMemo(() => {
    const printedChildIds = new Set();
    return sortedClassrooms.map((classroom) => {
        const draftIds = new Set(
          (draftAssignments[classroom.id] || []).map((child) => child.id),
        );
        const children = [
          ...(assignedChildren[classroom.id] || []).filter(
            (child) => isActiveChild(child) && !draftIds.has(child.id),
          ),
          ...(draftAssignments[classroom.id] || []).filter(isActiveChild),
        ]
          .filter((child) => {
            if (printedChildIds.has(child.id)) return false;
            printedChildIds.add(child.id);
            return true;
          })
          .sort((a, b) =>
            compareChildrenByExactBirthDate(a, b, currentLocale),
          );
        return { classroom, children };
      });
  }, [
      sortedClassrooms,
      assignedChildren,
      draftAssignments,
      currentLocale,
    ]);

  const printFlowAssignments = React.useMemo(() => {
    const seen = new Set();
    return [...flowPreviewAssignments]
      .filter(({ child }) => {
        if (!child?.id || seen.has(child.id)) return false;
        seen.add(child.id);
        return true;
      })
      .sort((a, b) =>
        compareChildrenByExactBirthDate(a.child, b.child, currentLocale),
      );
  }, [flowPreviewAssignments, currentLocale]);

  const formatPrintBirthDate = (value) => {
    const date = toDateSafe(value);
    return date
      ? new Intl.DateTimeFormat(currentLocale || "nl-NL").format(date)
      : "—";
  };

  const handlePrintOverview = () => printPage();

  // next year preview
  const currentDraftDetail =
    draftDetailState.detail || draftDetailState.summary || null;
  const currentDraftDetailId =
    draftDetailState.detail?.id || draftDetailState.summary?.id || null;

  const selectedYearLabel = React.useMemo(() => {
    if (!selectedYearObj) return "";
    if (selectedYearObj.label) return selectedYearObj.label;

    const startYear = getYearSafe(selectedYearObj.startDate);
    const endYear = getYearSafe(selectedYearObj.endDate);
    if (startYear != null && endYear != null) {
      return `${startYear} - ${endYear}`;
    }

    return t("common.not_available", { defaultValue: "Not available" });
  }, [selectedYearObj, t]);

  if (loading) return <Spinner />;
  if (error)
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <>
      <style>{`
        @keyframes lds-page-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes lds-content-enter {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-page-enter {
          animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > * {
          animation: lds-content-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .lds-page-enter > *:nth-child(2) {
          animation-delay: 45ms;
        }

        .lds-page-enter > *:nth-child(3) {
          animation-delay: 90ms;
        }

        .lds-page-enter > *:nth-child(4) {
          animation-delay: 135ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-page-enter,
          .lds-page-enter > * {
            animation: none;
          }
        }

        .classroom-overview-print {
          display: none;
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 14mm;
          }

          body > *:not(.classroom-overview-print) {
            display: none !important;
          }

          .classroom-overview-print,
          .classroom-overview-print * {
            visibility: visible !important;
          }

          .classroom-overview-print {
            display: block !important;
            position: static;
            width: 100%;
            color: #0f172a !important;
            background: white !important;
            font-family: Arial, sans-serif;
          }

          .classroom-overview-screen {
            display: none !important;
          }

          .classroom-print-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .classroom-print-row {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
      <div className="lds-page-enter">
    <PlacementPageShell>
      <PlacementHero
        eyebrow={t("placement.overview.title", { defaultValue: "Placement" })}
        title={t("classrooms.overview_management.title", {
          defaultValue: "Classroom overview",
        })}
        description={t("classrooms.overview_management.description", {
          defaultValue: "Assign, move and plan children by school year.",
        })}
        icon={<FaUsersCog aria-hidden="true" />}
        illustration={<FaUsersCog aria-hidden="true" />}
        badges={
          selectedYearObj ? (
            <span className="rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-500/40 dark:bg-slate-900/70 dark:text-indigo-200">
              {selectedYearLabel}
            </span>
          ) : undefined
        }
      />
      {/* 🛑 Desktop-only overlay voor mobiele layout */}
      {isMobileLayout && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/85 px-6 text-center text-white">
          <p className="mb-2 text-lg font-semibold">
            {t("waiting-list.mobile_not_supported_title", {
              defaultValue: "Niet beschikbaar op mobiel",
            })}
          </p>
          <p className="max-w-xs text-sm text-gray-200 mb-4">
            {t("waiting-list.mobile_not_supported_message", {
              defaultValue:
                "De wachtlijstpagina is alleen beschikbaar op een laptop of desktop voor voldoende overzicht.",
            })}
          </p>

          <button
            type="button"
            onClick={handleMobileBack}
            className="mt-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-gray-900 shadow hover:bg-white dark:bg-gray-100 dark:text-gray-900"
          >
            {t("common.back", { defaultValue: "Ga terug" })}
          </button>
        </div>
      )}

      {selectedYear && suspiciousChildIds.size > 0 && (
        <div
          role="alert"
          aria-live="polite"
          className="mb-4 p-4 border-l-4 rounded
                 border-yellow-500 dark:border-yellow-400
                 bg-yellow-50 dark:bg-yellow-900/60
                 text-yellow-900 dark:text-yellow-100"
        >
          {t("classrooms.overview_management.suspicious_detected_message")}
        </div>
      )}

      {!selectedYear ? (
        <section className="overflow-visible rounded-[28px] border border-indigo-100/80 bg-white/95 shadow-[0_24px_60px_rgba(15,23,42,0.10)] dark:border-indigo-500/30 dark:bg-slate-900/90">
          <div className="border-b border-indigo-100/70 bg-gradient-to-r from-indigo-50/90 via-white to-sky-50/80 px-6 py-5 dark:border-indigo-500/30 dark:from-indigo-950/50 dark:via-slate-900 dark:to-sky-950/40 sm:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.10em] text-indigo-600 dark:text-indigo-300">
              {t("classrooms.overview_management.setup_label", {
                defaultValue: "Planning setup",
              })}
            </p>
            <div className="mt-2 flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-200">
                <FaCalendarAlt aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                  {t("classrooms.overview_management.select_year", {
                    defaultValue: "Choose a school year",
                  })}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("classrooms.overview_management.select_year_description", {
                    defaultValue: "Make a deliberate choice before opening the classroom workspace.",
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 p-6 sm:p-8 lg:grid-cols-2">
            {[
              {
                key: "current",
                year: currentYearOption,
                eyebrow: t("classrooms.overview_management.current_year_label", {
                  defaultValue: "Current school year",
                }),
                description: t("classrooms.overview_management.current_year_choice_description", {
                  defaultValue: "Review and manage the active classroom placement.",
                }),
                tone: "indigo",
              },
              {
                key: "next",
                year: nextYearOption,
                eyebrow: t("classrooms.overview_management.next_year_label", {
                  defaultValue: "Next school year",
                }),
                description: t("classrooms.overview_management.next_year_choice_description", {
                  defaultValue: "Prepare the classroom placement for the coming school year.",
                }),
                tone: "sky",
              },
            ].map((option) => {
              const startYear = getYearSafe(option.year?.startDate);
              const endYear = getYearSafe(option.year?.endDate);
              const yearLabel =
                startYear != null && endYear != null
                  ? `${startYear} - ${endYear}`
                  : option.year?.label || "—";
              const isIndigo = option.tone === "indigo";

              return (
                <button
                  key={option.key}
                  type="button"
                  disabled={!option.year}
                  onClick={() => option.year && setSelectedYear(option.year.id)}
                  className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45 dark:focus:ring-offset-slate-900 ${
                    isIndigo
                      ? "border-indigo-200/80 bg-gradient-to-br from-indigo-50 via-white to-white hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-[0_16px_35px_rgba(79,70,229,0.16)] focus:ring-indigo-400 dark:border-indigo-500/40 dark:from-indigo-950/45 dark:via-slate-900 dark:to-slate-900"
                      : "border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-white hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-[0_16px_35px_rgba(14,165,233,0.16)] focus:ring-sky-400 dark:border-sky-500/40 dark:from-sky-950/45 dark:via-slate-900 dark:to-slate-900"
                  }`}
                >
                  <span
                    className={`text-xs font-bold uppercase tracking-[0.08em] ${
                      isIndigo
                        ? "text-indigo-600 dark:text-indigo-300"
                        : "text-sky-700 dark:text-sky-300"
                    }`}
                  >
                    {option.eyebrow}
                  </span>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <span className="text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                      {yearLabel}
                    </span>
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition group-hover:translate-x-0.5 ${
                        isIndigo
                          ? "bg-indigo-600 text-white dark:bg-indigo-500"
                          : "bg-sky-600 text-white dark:bg-sky-500"
                      }`}
                    >
                      <FaArrowRight aria-hidden="true" />
                    </span>
                  </div>
                  <p className="mt-2 max-w-md text-sm text-slate-600 dark:text-slate-300">
                    {option.description}
                  </p>
                  <span className="mt-4 inline-flex text-sm font-semibold text-slate-900 dark:text-white">
                    {t("classrooms.overview_management.open_year", {
                      defaultValue: "Open this school year",
                    })}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-200/80 bg-slate-50/70 px-6 py-5 dark:border-slate-700 dark:bg-slate-950/35 sm:px-8">
            <div className="max-w-2xl">
              <label className="block text-sm font-semibold text-slate-800 dark:text-slate-200">
                {t("classrooms.overview_management.other_school_year", {
                  defaultValue: "Choose another school year",
                })}
              </label>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("classrooms.overview_management.other_school_year_description", {
                  defaultValue: "Use the list for a previous or different school year.",
                })}
              </p>
              <div className="mt-3 max-w-xl">
                <SchoolYearDropdown
                  schoolYears={schoolYears}
                  selectedSchoolYearId={selectedYear ?? ""}
                  onChange={setSelectedYear}
                />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <DndProvider backend={HTML5Backend}>
          <div className="w-full space-y-5">
            <div className="classroom-overview-screen space-y-5">
            {/* Actions */}
            {selectedYear && (
              <div className="rounded-2xl border border-indigo-100/60 bg-gradient-to-br from-indigo-50/70 via-white to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/90 dark:to-gray-800 dark:shadow-none sm:p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  {/* Left buttons */}
                  <div className="flex flex-wrap gap-3">
                    {canManageClassrooms && (
                      <button
                        onClick={handleConfirmAssignments}
                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_25px_rgba(79,70,229,0.30)] transition hover:-translate-y-0.5 hover:shadow-[0_15px_30px_rgba(79,70,229,0.35)] focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:shadow-none"
                      >
                        {t("classrooms.overview_management.confirm_button")}
                      </button>
                    )}

                    <button
                      onClick={resetAssignments}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                    >
                      {t("common.reset")}
                    </button>

                    <button
                      onClick={openDraftForm}
                      disabled={
                        !hasPendingChanges || isSavingDraft || draftsLoading
                      }
                      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 ${
                        !hasPendingChanges || isSavingDraft || draftsLoading
                          ? "cursor-not-allowed bg-indigo-200 text-indigo-100 opacity-70 dark:bg-indigo-700/50 dark:text-indigo-200"
                          : "bg-gradient-to-r from-indigo-500 via-indigo-500 to-indigo-600 text-white shadow-[0_10px_25px_rgba(79,70,229,0.25)] hover:-translate-y-0.5 hover:shadow-[0_15px_35px_rgba(79,70,229,0.35)]"
                      }`}
                    >
                      {isSavingDraft
                        ? t("common.loading")
                        : t("classrooms.overview_management.save_draft_button")}
                    </button>

                    <button
                      onClick={handleRefreshDrafts}
                      disabled={draftsLoading}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:ring-offset-2 ${
                        draftsLoading
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500 opacity-70 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                          : "border-indigo-100 bg-white/80 text-indigo-700 shadow-sm hover:-translate-y-0.5 hover:bg-white dark:border-indigo-500/40 dark:bg-gray-900 dark:text-indigo-100"
                      }`}
                    >
                      {draftsLoading
                        ? t("classrooms.overview_management.drafts_loading")
                        : t("classrooms.overview_management.refresh_drafts")}
                    </button>

                    <button
                      type="button"
                      onClick={handlePrintOverview}
                      disabled={loadingFlow}
                      className="inline-flex items-center gap-2 rounded-xl border border-sky-200 bg-white/90 px-4 py-2 text-sm font-semibold text-sky-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0 dark:border-sky-500/40 dark:bg-slate-900 dark:text-sky-200 dark:hover:bg-slate-800"
                    >
                      <FaPrint aria-hidden="true" />
                      {loadingFlow
                        ? t("classrooms.overview_management.preparing_pdf", {
                            defaultValue: "Preparing PDF…",
                          })
                        : t("classrooms.overview_management.print_pdf", {
                            defaultValue: "Print / save as PDF",
                          })}
                    </button>
                  </div>

                  {/* Selected school year */}
                  <div className="flex shrink-0 items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2 dark:border-indigo-500/30 dark:bg-indigo-950/25">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-indigo-500 dark:text-indigo-300">
                        {t(
                          "classrooms.overview_management.current_year_label",
                          { defaultValue: "Selected school year" },
                        )}
                      </p>
                      <p className="mt-0.5 whitespace-nowrap text-sm font-bold text-slate-950 dark:text-white">
                        {selectedYearLabel}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedYear(null)}
                      className="inline-flex min-h-9 items-center rounded-lg border border-indigo-200 bg-white px-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:border-indigo-500/40 dark:bg-slate-900 dark:text-indigo-200 dark:hover:bg-slate-800"
                    >
                      {t("classrooms.overview_management.change_year_button", {
                        defaultValue: "Choose another school year",
                      })}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedYear && (
              <div className="mb-6 space-y-4">
                {draftFormOpen && (
                  <div className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                    <form onSubmit={handleSaveDraft} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {t(
                            "classrooms.overview_management.draft_save_modal_title",
                          )}
                        </h3>
                        <button
                          type="button"
                          onClick={closeDraftForm}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          {t("classrooms.overview_management.draft_name_label")}
                        </label>
                        <input
                          type="text"
                          value={draftFormLabel}
                          onChange={(e) => setDraftFormLabel(e.target.value)}
                          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          maxLength={120}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          {t(
                            "classrooms.overview_management.draft_notes_label",
                          )}
                        </label>
                        <textarea
                          value={draftFormNotes}
                          onChange={(e) => setDraftFormNotes(e.target.value)}
                          rows={3}
                          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {draftFormError && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {draftFormError}
                        </p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={closeDraftForm}
                          className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          type="submit"
                          disabled={isSavingDraft}
                          className={`px-4 py-2 rounded text-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                            isSavingDraft
                              ? "bg-teal-400 cursor-not-allowed opacity-70"
                              : "bg-teal-600 hover:bg-teal-700"
                          }`}
                        >
                          {isSavingDraft
                            ? t("common.loading")
                            : t(
                                "classrooms.overview_management.draft_save_submit",
                              )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="rounded-2xl border border-indigo-100/60 bg-gradient-to-br from-indigo-50/70 via-white to-white shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-indigo-100/70 dark:border-indigo-500/30">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t("classrooms.overview_management.drafts_heading")}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t("classrooms.overview_management.draft_count", {
                        count: draftSummaries.length,
                      })}
                    </span>
                  </div>
                  <div className="px-5 py-4 space-y-4">
                    {draftsLoading && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("classrooms.overview_management.drafts_loading")}
                      </p>
                    )}
                    {!draftsLoading && draftsError && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {draftsError.message}
                      </p>
                    )}
                    {!draftsLoading &&
                      !draftsError &&
                      draftSummaries.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t("classrooms.overview_management.no_drafts")}
                        </p>
                      )}
                    {!draftsLoading &&
                      !draftsError &&
                      draftSummaries.length > 0 && (
                        <ul className="space-y-3">
                          {draftSummaries.map((draft) => {
                            const label =
                              draft?.label ||
                              draft?.name ||
                              draft?.title ||
                              t(
                                "classrooms.overview_management.draft_fallback_label",
                                { id: draft?.id },
                              );
                            const createdAt =
                              draft?.createdAt ||
                              draft?.createdOn ||
                              draft?.created;
                            const creatorIdentifiers = [
                              draft?.createdBy,
                              draft?.createdById,
                              draft?.createdByID,
                              draft?.createdByUuid,
                              draft?.createdByUUID,
                              draft?.author,
                              draft?.authorId,
                            ];
                            const primaryCreatorIdentifier =
                              creatorIdentifiers.find(
                                (identifier) =>
                                  typeof identifier === "string" &&
                                  looksLikeUuid(identifier),
                              ) ||
                              creatorIdentifiers.find(
                                (identifier) => typeof identifier === "string",
                              );
                            const createdBy =
                              draft?.createdByDisplay ||
                              draft?.createdByName ||
                              resolveUserDisplayName(
                                primaryCreatorIdentifier,
                              ) ||
                              draft?.createdBy ||
                              draft?.author ||
                              null;
                            const stale =
                              draft?.stale ??
                              draft?.isStale ??
                              draft?.diff?.stale ??
                              false;
                            const changeSummary =
                              formatDraftChangeSummary(draft);

                            return (
                              <li
                                key={draft?.id || label}
                                className="flex flex-col gap-3 rounded-2xl border border-white/60 bg-white/90 p-4 shadow-sm ring-1 ring-gray-100/70 transition hover:-translate-y-0.5 hover:shadow-[0_15px_35px_rgba(15,23,42,0.12)] dark:border-gray-700/60 dark:bg-gray-900/70 dark:ring-gray-800/60 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div>
                                  <p className="flex flex-wrap items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                                    <span>{label}</span>
                                    <span
                                      className={`text-xs inline-flex items-center rounded px-2 py-0.5 ${
                                        stale
                                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-100"
                                          : "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200"
                                      }`}
                                    >
                                      {stale
                                        ? t(
                                            "classrooms.overview_management.draft_invalid_badge",
                                          )
                                        : t(
                                            "classrooms.overview_management.draft_valid_badge",
                                          )}
                                    </span>
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t(
                                      "classrooms.overview_management.draft_detail_created",
                                    )}
                                    : {formatDateTime(createdAt)}
                                  </p>
                                  {createdBy && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {t(
                                        "classrooms.overview_management.draft_detail_created_by",
                                      )}
                                      : {createdBy}
                                    </p>
                                  )}
                                  {changeSummary && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {changeSummary}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <button
                                    onClick={() => handleViewDraft(draft)}
                                    disabled={loadingDraftId === draft.id}
                                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                                      loadingDraftId === draft.id
                                        ? "cursor-not-allowed bg-indigo-300/70"
                                        : "bg-gradient-to-r from-indigo-500 to-indigo-400 shadow-[0_10px_25px_rgba(79,70,229,0.25)] hover:-translate-y-0.5 hover:shadow-[0_15px_35px_rgba(79,70,229,0.35)]"
                                    }`}
                                  >
                                    {loadingDraftId === draft.id
                                      ? t(
                                          "classrooms.overview_management.draft_loading",
                                        )
                                      : t(
                                          "classrooms.overview_management.view_draft",
                                        )}
                                  </button>
                                  <button
                                    onClick={() => handleApplyDraft(draft)}
                                    disabled={
                                      applyingDraftId === draft.id ||
                                      draft?.stale
                                    }
                                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                                      applyingDraftId === draft.id ||
                                      draft?.stale
                                        ? "cursor-not-allowed bg-indigo-300/70"
                                        : "bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-600 shadow-[0_10px_25px_rgba(79,70,229,0.25)] hover:-translate-y-0.5 hover:shadow-[0_15px_35px_rgba(79,70,229,0.35)]"
                                    }`}
                                  >
                                    {applyingDraftId === draft.id
                                      ? t(
                                          "classrooms.overview_management.draft_applying",
                                        )
                                      : t(
                                          "classrooms.overview_management.load_draft",
                                        )}
                                  </button>
                                  <button
                                    onClick={() => openDraftDeleteModal(draft)}
                                    disabled={deletingDraftId === draft.id}
                                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                                      deletingDraftId === draft.id
                                        ? "cursor-not-allowed bg-red-400/70"
                                        : "bg-gradient-to-r from-rose-600 to-red-500 shadow-[0_10px_25px_rgba(225,29,72,0.25)] hover:-translate-y-0.5 hover:shadow-[0_15px_35px_rgba(225,29,72,0.35)]"
                                    }`}
                                  >
                                    {deletingDraftId === draft.id
                                      ? t(
                                          "classrooms.overview_management.draft_deleting",
                                        )
                                      : t(
                                          "classrooms.overview_management.delete_draft",
                                        )}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Ongeplaatste kinderen + Wachtlijst */}
            <div
              className={`mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2 ${
                !selectedYear ? "pointer-events-none opacity-50" : ""
              }`}
            >
              {/* Unassigned */}
              <div className="rounded-2xl border border-indigo-100/60 bg-gradient-to-br from-indigo-50/70 via-white to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("classrooms.overview_management.unassigned")}
                  </h2>
                  <span className="text-xs uppercase tracking-wide text-indigo-500 dark:text-indigo-200">
                    {sortedUnassignedChildren.length}
                  </span>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/90 p-4 text-gray-800 shadow-sm dark:border-gray-700/60 dark:bg-gray-900/70 dark:text-gray-100">
                  {sortedUnassignedChildren.length > 0 ? (
                    <ul className="space-y-0">
                      {sortedUnassignedChildren.map((child) => {
                        const suggestedClassName = suggestClassroomForChild(
                          child,
                          classrooms,
                          selectedYearObj,
                        );

                        return (
                          <DraggableChild
                            key={child.id}
                            child={child}
                            suggestedClassName={suggestedClassName}
                            onResetChild={handleResetChild}
                            childOriginalLocation={childOriginalLocation}
                            isSuspicious={suspiciousChildIds.has(child.id)}
                            modifiedAssignments={modifiedAssignments}
                            draftAssignments={draftAssignments}
                            schoolYear={selectedYearObj}
                          />
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("classrooms.overview_management.no_unassigned")}
                    </p>
                  )}
                </div>
              </div>

              {/* Wachtlijst (sleepbare kinderen, uniek per kind) */}
              <div className="rounded-2xl border border-emerald-100/60 bg-gradient-to-br from-emerald-50/70 via-white to-white p-4 shadow-sm dark:border-emerald-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("classrooms.overview_management.waiting_list")}
                  </h2>
                  <span className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-200">
                    {totalWaitingChildren.length}
                  </span>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/90 p-4 text-gray-800 shadow-sm dark:border-gray-700/60 dark:bg-gray-900/70 dark:text-gray-100">
                  {hasAnyClassrooms ? (
                    totalWaitingChildren.length > 0 ? (
                      <ul>
                        {(() => {
                          const seen = new Set();
                          return Object.entries(waitingListChildren)
                            .flatMap(([, children]) => {
                              if (!Array.isArray(children)) return [];

                              return children
                                .filter((child) => {
                                  if (!isActiveChild(child)) return false;
                                  if (seen.has(child.id)) return false;
                                  seen.add(child.id);

                                  // Bereken leeftijd bij start schooljaar
                                  const ageAtStart = calculateAge(
                                    child.dateBirth,
                                    ageSortReferenceDate,
                                  );

                                  // Filter alleen kinderen die NIET in dat jaar 4 worden
                                  const birthYear = getYearSafe(
                                    child.dateBirth,
                                  );
                                  const schoolYearStart = getYearSafe(
                                    selectedYearObj?.startDate,
                                  );
                                  const turnsFourThisYear =
                                    birthYear != null &&
                                    schoolYearStart != null &&
                                    schoolYearStart - birthYear === 4;

                                  if (ageAtStart < 4 && !turnsFourThisYear) {
                                    return false; // te jong én wordt dat schooljaar ook geen 4
                                  }
                                  return true;
                                })
                                .map((child) => {
                                  const suggestedClassName =
                                    suggestClassroomForChild(
                                      child,
                                      classrooms,
                                      selectedYearObj,
                                    );
                                  return { child, suggestedClassName };
                                });
                            })
                            .sort((a, b) =>
                              compareChildrenByExactBirthDate(
                                a.child,
                                b.child,
                                currentLocale,
                              ),
                            )
                            .map(({ child, suggestedClassName }) => (
                              <DraggableChild
                                key={child.id}
                                child={child}
                                suggestedClassName={suggestedClassName}
                                onResetChild={handleResetChild}
                                showReset={
                                  childOriginalLocation[child.id]?.type !==
                                  "waiting"
                                }
                                childOriginalLocation={childOriginalLocation}
                                isSuspicious={suspiciousChildIds.has(child.id)}
                                modifiedAssignments={modifiedAssignments}
                                draftAssignments={draftAssignments}
                                schoolYear={selectedYearObj}
                              />
                            ));
                        })()}
                      </ul>
                    ) : (
                      <p className="text-sm italic text-gray-500 dark:text-gray-300">
                        {t(
                          "classrooms.overview_management.no_waiting_children",
                        )}
                      </p>
                    )
                  ) : (
                    <p className="text-sm italic text-gray-500 dark:text-gray-300">
                      {t(
                        "classrooms.overview_management.no_classrooms_for_waiting",
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Klassen */}
            {selectedYear && (
              <>
                <div className="mb-6 rounded-3xl border border-indigo-100/80 bg-gradient-to-br from-indigo-50/80 via-white to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t("classrooms.overview_management.kindergarten")}
                    </h2>
                    <span className="text-xs uppercase tracking-wide text-indigo-500 dark:text-indigo-200">
                      {kindergartenClassrooms.length}
                    </span>
                  </div>
                  {kindergartenClassrooms.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t(
                        "classrooms.overview_management.no_kindergarten_available",
                      )}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {kindergartenClassrooms.map((classroom) => (
                        <DroppableClassroom
                          key={classroom.id}
                          classroom={classroom}
                          onDropChild={handleDropChild}
                          draft={draftAssignments[classroom.id] || []}
                          assigned={assignedChildren[classroom.id] || []}
                          waiting={waitingListChildren[classroom.id] || []}
                          childOriginalLocation={childOriginalLocation}
                          onResetChild={handleResetChild}
                          onUnassignChild={(child) =>
                            handleUnassignChild(child, classroom.id)
                          }
                          suspiciousChildIds={suspiciousChildIds}
                          modifiedAssignments={modifiedAssignments}
                          draftAssignments={draftAssignments}
                          classrooms={classrooms}
                          selectedYearObj={selectedYearObj}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-6 rounded-3xl border border-indigo-100/70 bg-gradient-to-br from-white via-indigo-50/60 to-white shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowFlowPanel((value) => !value)}
                    className="flex w-full items-center justify-between rounded-3xl px-5 py-4 text-left text-gray-900 transition hover:bg-white/40 dark:text-gray-100 dark:hover:bg-gray-800/70"
                  >
                    <span className="font-semibold">
                      {t(
                        "classrooms.overview_management.flow_to_grade1",
                        "Doorstroom kleuters → 1e klas (volgend jaar)",
                      )}
                    </span>
                    {(() => {
                      const maleCount = flowCandidates.filter(
                        ({ child }) => child?.gender === "M",
                      ).length;
                      const femaleCount = flowCandidates.filter(
                        ({ child }) => child?.gender === "F",
                      ).length;
                      const otherCount =
                        flowCandidates.length - maleCount - femaleCount;
                      const total = flowCandidates.length || 1;
                      const malePercent = (maleCount / total) * 100;
                      const femalePercent = (femaleCount / total) * 100;
                      const otherPercent = Math.max(
                        0,
                        100 - malePercent - femalePercent,
                      );
                      return (
                        <span className="flex flex-col items-end gap-1 text-xs text-gray-700 dark:text-gray-200">
                          <span className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
                            <span className="inline-flex items-center gap-1 rounded bg-white/70 px-2 py-0.5 text-gray-700 shadow-sm dark:bg-gray-800/70 dark:text-gray-200">
                              {`${flowCandidates.length}/${totalCapacityNextYear ?? "—"}`}
                            </span>
                          </span>
                          <span className="flex h-1 w-40 overflow-hidden rounded bg-gray-200/80 dark:bg-gray-700">
                            <span
                              className="bg-blue-500"
                              style={{
                                width: `${malePercent}%`,
                                minWidth: maleCount > 0 ? "4px" : "0",
                              }}
                            />
                            <span
                              className="bg-pink-400"
                              style={{
                                width: `${femalePercent}%`,
                                minWidth: femaleCount > 0 ? "4px" : "0",
                              }}
                            />
                            <span
                              className="bg-purple-400"
                              style={{
                                width: `${otherPercent}%`,
                                minWidth: otherCount > 0 ? "4px" : "0",
                              }}
                            />
                          </span>
                        </span>
                      );
                    })()}
                  </button>
                  {showFlowPanel && (
                    <div className="border-t border-indigo-100/70 px-5 py-4 dark:border-indigo-500/30 dark:bg-gray-900/60">
                      {!nextYearObj ? (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {t(
                            "classrooms.overview_management.no_next_year",
                            "Geen volgend schooljaar gevonden.",
                          )}
                        </p>
                      ) : loadingFlow ? (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {t("common.loading", "Laden…")}
                        </p>
                      ) : flowCandidates.length === 0 ? (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {t(
                            "classrooms.overview_management.no_flow_candidates",
                            "Geen doorstroom-kandidaten.",
                          )}
                        </p>
                      ) : (
                        <div className="overflow-x-auto rounded-2xl border border-white/70 bg-white/80 dark:border-gray-700/60 dark:bg-gray-900/70">
                          <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-900 dark:divide-gray-700 dark:text-gray-100">
                            <thead className="indigo-table-head">
                              <tr>
                                <th className="px-3 py-2 font-medium">
                                  {t("common.child", "Kind")}
                                </th>
                                <th className="px-3 py-2 font-medium">
                                  {t("common.class_current", "Huidige klas")}
                                </th>
                                <th className="px-3 py-2 font-medium">
                                  {t(
                                    "classrooms.overview_management.age_at_next_start",
                                    "Leeftijd bij start volgend jaar",
                                  )}
                                </th>
                                <th className="px-3 py-2 font-medium">
                                  {t(
                                    "classrooms.overview_management.suggested_next",
                                    "Voorgestelde 1e klas",
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {flowPreviewAssignments.map(
                                ({
                                  child,
                                  from,
                                  target,
                                  capacity,
                                  assignedAfter,
                                }) => {
                                  const ageNext =
                                    nextYearObj?.startDate != null
                                      ? ageAt(
                                          child.dateBirth,
                                          nextYearObj.startDate,
                                        )
                                      : null;
                                  return (
                                    <tr
                                      key={child.id}
                                      className="even:bg-gray-50 dark:even:bg-gray-900/40"
                                    >
                                      <td className="px-3 py-2">
                                        <span className="inline-flex items-center gap-2">
                                          <GenderIcon gender={child.gender} />
                                          <span>
                                            {buildChildLabel(child) ||
                                              t("common.not_available")}
                                          </span>
                                        </span>
                                      </td>
                                      <td className="px-3 py-2">
                                        {translateClassName(from)}
                                      </td>
                                      <td className="px-3 py-2">
                                        {ageNext != null ? ageNext : "—"}
                                      </td>
                                      <td className="px-3 py-2">
                                        {target ? (
                                          <span className="inline-flex items-center gap-2">
                                            <span>
                                              {translateClassName(target)}
                                              {capacity != null
                                                ? ` (${assignedAfter ?? 0}/${capacity})`
                                                : ""}
                                            </span>
                                          </span>
                                        ) : (
                                          t(
                                            "classrooms.overview_management.no_grade1_found",
                                            "Geen 1e klas gevonden",
                                          )
                                        )}
                                      </td>
                                    </tr>
                                  );
                                },
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-6 rounded-3xl border border-indigo-100/80 bg-gradient-to-br from-white via-slate-50 to-white p-4 shadow-sm dark:border-indigo-500/30 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900/95 dark:to-gray-800 sm:p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t("classrooms.overview_management.grades")}
                    </h2>
                    <span className="text-xs uppercase tracking-wide text-indigo-500 dark:text-indigo-200">
                      {gradeClassrooms.length}
                    </span>
                  </div>
                  {gradeClassrooms.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("classrooms.overview_management.no_grades_available")}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {gradeClassrooms.map((classroom) => (
                        <DroppableClassroom
                          key={classroom.id}
                          classroom={classroom}
                          onDropChild={handleDropChild}
                          draft={draftAssignments[classroom.id] || []}
                          assigned={assignedChildren[classroom.id] || []}
                          waiting={waitingListChildren[classroom.id] || []}
                          childOriginalLocation={childOriginalLocation}
                          onUnassignChild={(child) =>
                            handleUnassignChild(child, classroom.id)
                          }
                          onResetChild={handleResetChild}
                          suspiciousChildIds={suspiciousChildIds}
                          modifiedAssignments={modifiedAssignments}
                          draftAssignments={draftAssignments}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            </div>

            {selectedYear && createPortal(
              <section className="classroom-overview-print">
                <header className="mb-6 border-b-2 border-slate-900 pb-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                    {t("classrooms.overview_management.print_eyebrow", {
                      defaultValue: "Classroom overview",
                    })}
                  </p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-950">
                    {selectedYearLabel}
                  </h1>
                  <p className="mt-1 text-xs text-slate-500">
                    {t("classrooms.overview_management.print_created", {
                      defaultValue: "Created on {{date}}",
                      date: new Intl.DateTimeFormat(currentLocale || "nl-NL").format(
                        new Date(),
                      ),
                    })}
                  </p>
                </header>

                {[
                  {
                    key: "interest",
                    title: t("classrooms.overview_management.waiting_list", {
                      defaultValue: "Interest list",
                    }),
                    children: totalWaitingChildren,
                  },
                  {
                    key: "unassigned",
                    title: t("classrooms.overview_management.unassigned", {
                      defaultValue: "Unassigned children",
                    }),
                    children: sortedUnassignedChildren,
                  },
                ].map((section) => (
                  <section
                    key={section.key}
                    className="mb-6"
                  >
                    <div className="mb-2 flex items-end justify-between border-b border-slate-400 pb-1">
                      <h2 className="text-base font-bold">{section.title}</h2>
                      <span className="text-xs font-semibold text-slate-600">
                        {section.children.length}
                      </span>
                    </div>
                    {section.children.length > 0 ? (
                      <table className="w-full border-collapse text-xs">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="w-10 border-b border-slate-300 py-1">#</th>
                            <th className="border-b border-slate-300 py-1">
                              {t("futurePlanning.table.child", {
                                defaultValue: "Child",
                              })}
                            </th>
                            <th className="w-32 border-b border-slate-300 py-1">
                              {t("children.fields.dateBirth", {
                                defaultValue: "Date of birth",
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.children.map((child, index) => (
                            <tr key={child.id} className="classroom-print-row">
                              <td className="border-b border-slate-200 py-1.5 text-slate-500">
                                {index + 1}
                              </td>
                              <td className="border-b border-slate-200 py-1.5 font-medium">
                                {buildChildLabel(child)}
                              </td>
                              <td className="border-b border-slate-200 py-1.5">
                                {formatPrintBirthDate(child.dateBirth)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="py-2 text-xs text-slate-500">
                        {t("common.no_results", { defaultValue: "No results." })}
                      </p>
                    )}
                  </section>
                ))}

                <section className="mb-6">
                  <div className="mb-2 flex items-end justify-between border-b border-slate-400 pb-1">
                    <div>
                      <h2 className="text-base font-bold">
                        {t(
                          "classrooms.overview_management.flow_to_grade1",
                          { defaultValue: "Kindergarten to first grade progression" },
                        )}
                      </h2>
                      {nextYearObj && (
                        <p className="text-[10px] text-slate-500">
                          {nextYearObj.label || nextYearObj.name ||
                            `${getYearSafe(nextYearObj.startDate) ?? "—"} - ${
                              getYearSafe(nextYearObj.endDate) ?? "—"
                            }`}
                        </p>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-slate-600">
                      {printFlowAssignments.length}
                    </span>
                  </div>
                  {!nextYearObj ? (
                    <p className="py-2 text-xs text-slate-500">
                      {t("classrooms.overview_management.no_next_year", {
                        defaultValue: "No next school year found.",
                      })}
                    </p>
                  ) : printFlowAssignments.length > 0 ? (
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="border-b border-slate-300 py-1">
                            {t("common.child", { defaultValue: "Child" })}
                          </th>
                          <th className="border-b border-slate-300 py-1">
                            {t("common.class_current", {
                              defaultValue: "Current classroom",
                            })}
                          </th>
                          <th className="w-24 border-b border-slate-300 py-1">
                            {t(
                              "classrooms.overview_management.age_at_next_start",
                              { defaultValue: "Age at next start" },
                            )}
                          </th>
                          <th className="border-b border-slate-300 py-1">
                            {t(
                              "classrooms.overview_management.suggested_next",
                              { defaultValue: "Suggested first grade" },
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {printFlowAssignments.map(({ child, from, target }) => {
                          const ageNext = nextYearObj?.startDate
                            ? ageAt(child.dateBirth, nextYearObj.startDate)
                            : null;
                          return (
                            <tr key={child.id} className="classroom-print-row">
                              <td className="border-b border-slate-200 py-1.5 font-medium">
                                {buildChildLabel(child)}
                              </td>
                              <td className="border-b border-slate-200 py-1.5">
                                {translateClassName(from) || "—"}
                              </td>
                              <td className="border-b border-slate-200 py-1.5">
                                {ageNext ?? "—"}
                              </td>
                              <td className="border-b border-slate-200 py-1.5">
                                {target ? translateClassName(target) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="py-2 text-xs text-slate-500">
                      {t(
                        "classrooms.overview_management.no_flow_candidates",
                        { defaultValue: "No progression candidates." },
                      )}
                    </p>
                  )}
                </section>

                <div className="grid grid-cols-2 gap-4">
                  {printClassrooms.map(({ classroom, children }) => (
                    <section
                      key={classroom.id}
                      className="classroom-print-section rounded border border-slate-400 p-3"
                    >
                      <div className="mb-2 flex items-start justify-between gap-3 border-b border-slate-300 pb-2">
                        <div>
                          <h2 className="text-sm font-bold">
                            {translateClassName(classroom.className, t)}
                          </h2>
                          {classroom.teacherName && (
                            <p className="text-[10px] text-slate-500">
                              {classroom.teacherName}
                            </p>
                          )}
                        </div>
                        <span className="text-xs font-bold">
                          {children.length}/{getCapacity(classroom) ?? "—"}
                        </span>
                      </div>
                      {children.length > 0 ? (
                        <ol className="space-y-1 text-[11px]">
                          {children.map((child) => (
                            <li
                              key={child.id}
                              className="classroom-print-row flex justify-between gap-3 border-b border-slate-200 pb-1"
                            >
                              <span>{buildChildLabel(child)}</span>
                              <span className="shrink-0 text-slate-500">
                                {formatPrintBirthDate(child.dateBirth)}
                              </span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-[11px] text-slate-500">
                          {t("classrooms.overview_management.no_children", {
                            defaultValue: "No children assigned.",
                          })}
                        </p>
                      )}
                    </section>
                  ))}
                </div>
              </section>,
              document.body,
            )}
          </div>
        </DndProvider>
      )}

      {draftDetailState.open && (
        <Modal
          show={draftDetailState.open}
          onClose={closeDraftDetail}
          type={
            draftDetailState.detail?.stale ||
            draftDetailState.detail?.diff?.stale
              ? "warning"
              : "info"
          }
          title={
            draftDetailState.detail?.label ||
            draftDetailState.detail?.name ||
            draftDetailState.summary?.label ||
            draftDetailState.summary?.name ||
            t("classrooms.overview_management.draft_detail_title")
          }
          footer={
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDraftDetail}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t("common.close", "Close")}
              </button>
              <button
                type="button"
                onClick={() => handleApplyDraft(draftDetailState.summary)}
                disabled={
                  !currentDraftDetailId ||
                  applyingDraftId === currentDraftDetailId ||
                  draftDetailState.detail?.diff?.stale ||
                  draftDetailState.detail?.stale ||
                  draftDetailState.summary?.stale
                }
                className={`px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  !currentDraftDetailId ||
                  applyingDraftId === currentDraftDetailId ||
                  draftDetailState.detail?.diff?.stale ||
                  draftDetailState.detail?.stale ||
                  draftDetailState.summary?.stale
                    ? "bg-indigo-300 text-indigo-100 cursor-not-allowed opacity-70"
                    : "bg-indigo-500 text-white hover:bg-indigo-600"
                }`}
              >
                {applyingDraftId === currentDraftDetailId
                  ? t("classrooms.overview_management.draft_applying")
                  : t("classrooms.overview_management.load_draft")}
              </button>
            </div>
          }
        >
          {draftDetailState.loading ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t("classrooms.overview_management.drafts_loading")}
            </p>
          ) : (
            (() => {
              const detail = draftDetailState.detail || {};
              const summary = draftDetailState.summary || {};
              const createdAt =
                detail.createdAt ||
                detail.createdOn ||
                summary.createdAt ||
                summary.createdOn ||
                null;
              const creatorIdentifiers = [
                detail.createdByDisplay,
                summary.createdByDisplay,
                detail.createdBy,
                detail.createdById,
                detail.createdByID,
                detail.createdByUuid,
                detail.createdByUUID,
                summary.createdBy,
                summary.createdById,
                summary.createdByID,
                summary.createdByUuid,
                summary.createdByUUID,
                detail.author,
                summary.author,
              ].filter((value) => value != null);
              const primaryCreatorIdentifier =
                creatorIdentifiers.find(
                  (identifier) =>
                    typeof identifier === "string" && looksLikeUuid(identifier),
                ) ||
                creatorIdentifiers.find(
                  (identifier) => typeof identifier === "string",
                );
              const createdBy =
                detail.createdByDisplay ||
                summary.createdByDisplay ||
                detail.createdByName ||
                summary.createdByName ||
                resolveUserDisplayName(primaryCreatorIdentifier) ||
                detail.createdBy ||
                summary.createdBy ||
                detail.author ||
                summary.author ||
                null;
              const notes =
                detail.notes || detail.description || detail.comment || "";
              const stale =
                detail.stale ??
                detail.diff?.stale ??
                summary.stale ??
                summary.isStale ??
                false;
              const diffChanges = Array.isArray(detail?.diff?.changes)
                ? detail.diff.changes.filter(Boolean)
                : Array.isArray(detail?.changes)
                  ? detail.changes.filter(Boolean)
                  : [];
              const changeTypeLabels = {
                ADD: t("classrooms.overview_management.draft_change_add"),
                MOVE: t("classrooms.overview_management.draft_change_move"),
                REMOVE: t("classrooms.overview_management.draft_change_remove"),
              };
              const changeTypeStyles = {
                ADDED:
                  "bg-green-100 text-green-700 dark:bg-green-900/60 dark:text-green-200",
                MOVED:
                  "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200",
                REMOVE:
                  "bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200",
              };

              return (
                <div className="text-left text-sm text-gray-700 dark:text-gray-300 space-y-4">
                  {stale && (
                    <div className="px-3 py-2 rounded border border-yellow-400 bg-yellow-50 text-yellow-800 dark:border-yellow-500 dark:bg-yellow-900/40 dark:text-yellow-100">
                      {t("classrooms.overview_management.draft_detail_stale")}
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {t("classrooms.overview_management.draft_detail_created")}
                      :{" "}
                      <span className="font-normal text-gray-700 dark:text-gray-300">
                        {formatDateTime(createdAt)}
                      </span>
                    </div>
                    {createdBy && (
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {t(
                          "classrooms.overview_management.draft_detail_created_by",
                        )}
                        :{" "}
                        <span className="font-normal text-gray-700 dark:text-gray-300">
                          {createdBy}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      {t("classrooms.overview_management.draft_detail_notes")}
                    </h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {notes
                        ? notes
                        : t(
                            "classrooms.overview_management.draft_detail_no_notes",
                          )}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t(
                        "classrooms.overview_management.draft_detail_changes_heading",
                      )}
                    </h4>
                    {diffChanges.length === 0 ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t(
                          "classrooms.overview_management.draft_detail_no_changes",
                        )}
                      </p>
                    ) : (
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {diffChanges.map((change, index) => {
                          const type = String(
                            change?.type || change?.changeType || "",
                          ).toUpperCase();
                          const typeLabel =
                            changeTypeLabels[type] ||
                            type ||
                            t(
                              "classrooms.overview_management.draft_change_generic",
                            );
                          const badgeClass =
                            changeTypeStyles[type] ||
                            "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
                          const resolveClassroomName = (direction) => {
                            const idCandidates =
                              direction === "from"
                                ? [
                                    change?.fromClassroomId,
                                    change?.fromClassroom?.id,
                                    change?.from?.classroomId,
                                    change?.from?.id,
                                    change?.fromClassroomUuid,
                                    change?.fromClassroomUUID,
                                  ]
                                : [
                                    change?.toClassroomId,
                                    change?.toClassroom?.id,
                                    change?.to?.classroomId,
                                    change?.to?.id,
                                    change?.toClassroomUuid,
                                    change?.toClassroomUUID,
                                  ];

                            for (const candidate of idCandidates) {
                              if (!candidate) continue;
                              const classroom = classrooms.find(
                                (cls) => String(cls?.id) === String(candidate),
                              );
                              if (classroom) {
                                const translated =
                                  translateClassName(classroom);
                                if (translated && translated.trim())
                                  return translated.trim();
                                if (
                                  classroom?.className &&
                                  classroom.className.trim()
                                )
                                  return classroom.className.trim();
                                if (classroom?.name && classroom.name.trim())
                                  return classroom.name.trim();
                                return String(candidate);
                              }
                            }

                            const nameCandidates =
                              direction === "from"
                                ? [
                                    change?.fromClassroomName,
                                    change?.fromClassroom?.name,
                                    change?.from?.name,
                                    change?.fromClassroom?.className,
                                    change?.from?.className,
                                  ]
                                : [
                                    change?.toClassroomName,
                                    change?.toClassroom?.name,
                                    change?.to?.name,
                                    change?.toClassroom?.className,
                                    change?.to?.className,
                                  ];

                            for (const name of nameCandidates) {
                              if (typeof name === "string" && name.trim()) {
                                return name.trim();
                              }
                            }

                            const fallbackId = idCandidates.find(
                              (candidate) => candidate != null,
                            );
                            return fallbackId != null ? String(fallbackId) : "";
                          };

                          const identifierCandidates = [
                            change?.childId,
                            change?.childID,
                            change?.childUuid,
                            change?.childUUID,
                            change?.child?.id,
                            change?.child?.childId,
                            change?.child?.childID,
                            change?.child?.uuid,
                            change?.child?.childUuid,
                            change?.child?.childUUID,
                          ];
                          const resolvedChild =
                            resolveChildFromCandidates(identifierCandidates) ||
                            change?.child ||
                            null;
                          const derivedName =
                            getChildDisplayName(resolvedChild);
                          const childName =
                            change?.childName ||
                            derivedName ||
                            change?.child?.name ||
                            [change?.childFirstName, change?.childLastName]
                              .filter(Boolean)
                              .join(" ") ||
                            change?.childFullName ||
                            (() => {
                              const fallbackId =
                                change?.childUuid ??
                                change?.childUUID ??
                                change?.child?.uuid ??
                                change?.child?.id ??
                                change?.childId ??
                                change?.childID ??
                                null;
                              const fallbackString =
                                fallbackId != null
                                  ? String(fallbackId)
                                  : String(index + 1);
                              return `${t("classrooms.overview_management.draft_detail_child")} #${fallbackString}`;
                            })();
                          const fromName =
                            change?.fromClassroomName ||
                            change?.fromClassroom ||
                            change?.fromName ||
                            change?.from ||
                            "";
                          const toName =
                            change?.toClassroomName ||
                            change?.toClassroom ||
                            change?.toName ||
                            change?.to ||
                            "";
                          return (
                            <li
                              key={`${change?.childId ?? index}-${index}`}
                              className="py-2 space-y-1"
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`text-xs font-semibold rounded px-2 py-0.5 ${badgeClass}`}
                                >
                                  {typeLabel}
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {childName}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                                <span>
                                  {t(
                                    "classrooms.overview_management.draft_detail_from",
                                  )}
                                  :{" "}
                                  {resolveClassroomName("from") ||
                                    t("common.not_available")}
                                </span>
                                <span>
                                  {t(
                                    "classrooms.overview_management.draft_detail_to",
                                  )}
                                  :{" "}
                                  {resolveClassroomName("to") ||
                                    t("common.not_available")}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </Modal>
      )}

      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        status={modal.status}
        message={modal.message}
        onClose={() =>
          setModal({ show: false, type: "", title: "", message: "" })
        }
      />

      {deleteConfirm.show && (
        <Modal
          show={deleteConfirm.show}
          onClose={closeDraftDeleteModal}
          type="confirm"
          title={t(
            "classrooms.overview_management.draft_delete_confirm_title",
            { label: deleteConfirm.label },
          )}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDraftDeleteModal}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() =>
                  executeDeleteDraft(deleteConfirmState.currentDraft)
                }
                disabled={deletingDraftId === deleteConfirm.draftId}
                className={`px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  deletingDraftId === deleteConfirm.draftId
                    ? "bg-red-400 text-white cursor-not-allowed opacity-70"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {deletingDraftId === deleteConfirm.draftId
                  ? t("classrooms.overview_management.draft_deleting")
                  : t("classrooms.overview_management.delete_draft")}
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("classrooms.overview_management.draft_delete_confirm_message", {
              label: deleteConfirm.label,
            })}
          </p>
        </Modal>
      )}
    </PlacementPageShell>

      </div>
    </>
  );
};

export default ClassroomOverview;
